import React, { useState, useEffect, useReducer, useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './global.scss';
import { AppState, AppAction, Sheet, Statistics, Pouch, Group, POUCH_COLORS, GROUP_COLORS, HistoryActionType, SheetHistoryEntry } from './types';
import { initializeData, SheetStorage, SettingsStorage } from './utils/StorageUtils';
import ActiveSheetComponent, { ActiveSheetComponentRef } from './components/ActiveSheetComponent';
import { selectSheet, updateSheetTitle, deleteSheet } from './logic/SheetLogic';
import { calculateStatistics, getPouchActualValue, snapBalancingTotalDiff } from './logic/StatisticsLogic';
import { updatePouches } from './logic/PouchLogic';
import { repairGroupPaths } from './logic/GroupLogic';
import { getInitialSheets, createAndSaveNewSheet, createAndSaveInitializationSheet } from './utils/DefaultSheetUtils';
import { NumberCounter } from './components/NumberCounterComponent';
import HistoryComponent from './components/HistoryComponent';
import { HistoryLogic } from './utils/HistoryLogic';
import TableComponent from './components/TableComponent';
import { shareGroupContent, isInMuteGroup, clearGroupLogicCache, clearGTPRelatedCache, clearGroupResizeCache, changeGroupColor, sheetContributingPouchesAreAllTime } from './logic/GroupLogic';
import { applyBalancingPouch, calculateSheetTotal, getPouchEffectiveContribution, getTimePouchEffectiveForTotal } from './logic/BalancingPouchLogic';
import { LegendsComponent } from './components/LegendsComponent';
import { calculateColorTotal, calculateColorCount } from './logic/LegendsLogic';
import { useModifierKeyClasses } from './hooks/useModifierKeyClasses';
import packageJson from '../package.json';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { formatTooltip } from './utils/tooltipFormatter';
import { GuideContext } from './contexts/GuideContext';

// OS別修飾キー表記の取得関数
export const getModifierKeys = () => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  
  return {
    ctrlOption: isMac ? '⌥' : 'Ctrl',      // Mac: Option, Windows/Linux: Ctrl
    shift: isMac ? '⇧' : 'Shift',          // Mac: ⇧, Windows/Linux: Shift
    altControl: isMac ? '⌃' : 'Alt'        // Mac: Control, Windows/Linux: Alt
  };
};

const openExternal = (url: string) => {
  try {
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const plugins = cap.Plugins;
      if (plugins?.Browser?.open) {
        plugins.Browser.open({ url });
        return;
      }
    }
  } catch {}
  window.open(url, '_blank');
};

const formatDetailedNumber = (value: number): string => {
  const r8 = Math.round(value * 1e8) / 1e8;
  const r9 = Math.round(value * 1e9) / 1e9;
  const hasMore = Math.abs(r9 - r8) > 0;
  const intPart = Math.floor(Math.abs(r8));
  const decPart = Math.abs(r8) - intPart;
  const sign = r8 < 0 ? '-' : '';
  let result = decPart > 0
    ? `${sign}${intPart.toLocaleString()}.${decPart.toFixed(8).replace(/0+$/, '').substring(2)}`
    : `${sign}${intPart.toLocaleString()}`;
  if (hasMore) result += '...';
  return result;
};

// PouchWithDrag型を定義（ActiveSheetComponentと同じ定義）
interface PouchWithDrag extends Pouch {
  _isDragging?: boolean;
  _isMoving?: boolean; // パウチの移動フラグを追加
}

// 初期状態
export const initialState: AppState = {
  sheets: [],
  activeSheetId: '',
  settings: {
    buttonsVisible: true,
    posinegaVisible: true,
    defaultValue: 0,
    autoHideMenu: false,
    isLeftSidebarOpen: window.innerWidth > 768,
    isRightSidebarOpen: window.innerWidth > 768
  },
  isMobile: window.innerWidth <= 768,
  isLeftSidebarOpen: true,
  isRightSidebarOpen: false,
  isRightSidebarFullyClosed: true, // 初期状態では完全に閉じている
  isTouchDevice: ('ontouchstart' in window) || navigator.maxTouchPoints > 0

};

// Reducer関数
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'INIT_APP':
      return {
        ...state,
        sheets: action.payload.sheets,
        settings: action.payload.settings
      };
      
    case 'SET_ACTIVE_SHEET':
      const newActiveSheet = state.sheets.find(s => s.id === action.payload);
      return {
        ...state,
        activeSheetId: action.payload
      };
      
    case 'ADD_SHEET':
      return {
        ...state,
        sheets: [action.payload, ...state.sheets]
      };
      
      case 'UPDATE_SHEET':
        const updatedSheets = state.sheets.map(sheet => 
          sheet.id === action.payload.id ? action.payload : sheet
        );
        
        return {
          ...state,
          sheets: updatedSheets
        };
      
    case 'DELETE_SHEET':
      return {
        ...state,
        sheets: state.sheets.filter(sheet => sheet.id !== action.payload),
        activeSheetId: state.activeSheetId === action.payload 
          ? (state.sheets.length > 1 
            ? state.sheets.find(s => s.id !== action.payload)?.id || '' 
            : '')
          : state.activeSheetId
      };
      
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: action.payload
      };
      
    case 'TOGGLE_MENU':
      return {
        ...state,
        isLeftSidebarOpen: action.payload !== undefined ? action.payload : !state.isLeftSidebarOpen,
        isRightSidebarOpen: state.isMobile ? false : state.isRightSidebarOpen,
        settings: {
          ...state.settings,
          isLeftSidebarOpen: action.payload !== undefined ? action.payload : !state.isLeftSidebarOpen
        }
      };
      
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        isRightSidebarOpen: action.payload !== undefined ? action.payload : !state.isRightSidebarOpen,
        isLeftSidebarOpen: state.isMobile ? false : state.isLeftSidebarOpen,
        isRightSidebarFullyClosed: false, // サイドバーが開かれた場合は即座にfalse
        settings: {
          ...state.settings,
          isRightSidebarOpen: action.payload !== undefined ? action.payload : !state.isRightSidebarOpen
        }
      };
      
    case 'SET_RIGHT_SIDEBAR_FULLY_CLOSED':
      return {
        ...state,
        isRightSidebarFullyClosed: action.payload
      };
    
    case 'SET_MOBILE_STATE':
      return {
        ...state,
        isMobile: action.payload
      };
      
    case 'SET_ACTIVE_SHEET_POUCHES':
      return {
        ...state,
        sheets: state.sheets.map(sheet => 
          sheet.id === state.activeSheetId ? { ...sheet, pouches: action.payload } : sheet
        )
      };
      
    case 'SET_ACTIVE_SHEET_GROUPS':
      return {
        ...state,
        sheets: state.sheets.map(sheet => 
          sheet.id === state.activeSheetId ? { ...sheet, groups: action.payload } : sheet
        )
      };
      
    default:
      return state;
  }
};

// デバウンス用のカスタムフック
const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef<number | null>(null);

  return useCallback((...args: any[]) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      callback(...args);
      timeoutRef.current = null;
    }, delay);
  }, [callback, delay]);
};

function App() {
  // 修飾キーの状態を追跡してHTMLにクラスを付ける
  useModifierKeyClasses();
  
  // アプリのメインReducer
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // 統計情報
  const [stats, setStats] = useState<Statistics>({
    count: 0,
    total: 0,
    average: 0,
    max: 0,
    min: 0
  });
  
  // 合計表示の展開状態を管理
  const [isTotalExpanded, setIsTotalExpanded] = useState(false);
  
  // グリッドスナップの有効/無効を管理（デフォルトはオン、localStorage永続化）
  const [isSnapEnabled, setIsSnapEnabled] = useState(() => {
    const saved = localStorage.getItem('pouches-grid-enabled');
    return saved !== null ? saved === 'true' : true;
  });

  // ツールチップガイドの有効/無効を管理（デフォルトはオン、localStorage永続化）
  const [isGuideEnabled, setIsGuideEnabled] = useState(() => {
    const saved = localStorage.getItem('pouches-guide-enabled');
    const value = saved !== null ? saved === 'true' : true;
    (window as any).__isGuideEnabled = value;
    return value;
  });
  
  // スナップ状態をグローバルに同期し、グリッド表示も制御
  useEffect(() => {
    window.__isSnapEnabled = isSnapEnabled;
    localStorage.setItem('pouches-grid-enabled', String(isSnapEnabled));
    if (isSnapEnabled) {
      document.body.classList.remove('grid-disabled');
    } else {
      document.body.classList.add('grid-disabled');
    }
  }, [isSnapEnabled]);

  // ガイド状態をlocalStorageに永続化
  useEffect(() => {
    localStorage.setItem('pouches-guide-enabled', String(isGuideEnabled));
  }, [isGuideEnabled]);
  
  // シート切替フラグをstateとrefの両方で管理
  const [isSheetSwitching, setIsSheetSwitching] = useState(false);
  const isSheetSwitchingRef = useRef(false);
  const switchTimerRef = useRef<number | null>(null);
  
  // グループ更新用のデバウンスref
  const groupUpdateTimeoutRef = useRef<number | null>(null);
  const pendingGroupUpdateRef = useRef<Group[] | null>(null);
  
  // パウチ更新用のデバウンスref
  const pouchUpdateTimeoutRef = useRef<number | null>(null);
  const pendingPouchUpdateRef = useRef<Pouch[] | null>(null);
  
  // handleUpdateActiveSheetの並行実行を防ぐためのフラグ
  const isUpdatingActiveSheetRef = useRef<boolean>(false);
  
  // 最新のstateを常に保持するref（クロージャ問題を回避）
  const stateRef = useRef(state);
  const activeSheetFilePathRef = useRef<Map<string, string>>(new Map());
  
  // 履歴作成処理中のフラグとタイマー
  const isInHistoryActionRef = useRef<boolean>(false);
  const historyActionTimerRef = useRef<number | null>(null);
  
  // リロード時アニメーションフラグを追加
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // ローディング状態を追跡してHTMLにクラスを付ける
  useEffect(() => {
    const htmlElement = document.documentElement;
    
    if (isInitialLoading || isSheetSwitching) {
      htmlElement.classList.add('loading');
    } else {
      htmlElement.classList.remove('loading');
    }
  }, [isInitialLoading, isSheetSwitching]);
  
  // シート間ドラッグ移動用の状態管理
  const [isDraggingToSheet, setIsDraggingToSheet] = useState(false);
  const [hoveredSheetId, setHoveredSheetId] = useState<string | null>(null);
  const sheetHoverTimerRef = useRef<number | null>(null);
  
  // シート間ドラッグ用のref
  const draggedGroupDataRef = useRef<{
    group: Group;
    pouches: Pouch[];
    containedGroups: Group[];
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  } | null>(null);
  const sourceSheetGroupRef = useRef<Group | null>(null);
  const draggedPouchDataRef = useRef<{
    pouch: Pouch;
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  } | null>(null);
  const sourceSheetPouchRef = useRef<Pouch | null>(null);

  // シート間ドラッグ状態をリセットする関数
  const resetSheetDragState = useCallback(() => {
    setIsDraggingToSheet(false);
    draggedGroupDataRef.current = null;
    draggedPouchDataRef.current = null;
    setHoveredSheetId(null);
    if (sheetHoverTimerRef.current) {
      clearTimeout(sheetHoverTimerRef.current);
      sheetHoverTimerRef.current = null;
    }
  }, []);
  const activeSheetComponentRef = useRef<any>(null);
  const globalInteractionRef = useRef<any>(null);
  
  // シート切り替え時のドラッグ状態を保持するためのref
  const preservedDragStateRef = useRef<{
    interaction: any;
    draggedFromOtherSheet: boolean;
    draggedGroupId?: string;
    tempPouchPosition?: { x: number; y: number };
    draggedGroupData?: any;
    draggedPouchData?: any;
  } | null>(null);

  // 元シートからグループを削除する関数
  const removeGroupFromSourceSheet = useCallback(async (groupId: string, sourceSheetId: string) => {
    try {
      // 元シートのデータを取得
      const sourceSheet = state.sheets.find(sheet => sheet.id === sourceSheetId);
      if (!sourceSheet) return;

      // ドラッグされた実際の要素情報を取得
      const draggedData = draggedGroupDataRef.current;
      if (!draggedData) {
        console.warn('ドラッグされた要素の情報が見つかりません');
        return;
      }

      // 実際に移動されたグループIDリストを作成（メイングループ + 内包グループ）
      const groupIdsToDelete = [draggedData.group.id];
      if (draggedData.containedGroups && draggedData.containedGroups.length > 0) {
        const containedGroupIds = draggedData.containedGroups.map(g => g.id);
        groupIdsToDelete.push(...containedGroupIds);
      }

      // 実際に移動されたパウチIDリストを作成
      const pouchIdsToDelete = draggedData.pouches ? draggedData.pouches.map(p => p.id) : [];

      // パウチを削除（実際に移動されたパウチのみ）
      const updatedPouches = sourceSheet.pouches.filter(pouch => !pouchIdsToDelete.includes(pouch.id));

      // グループを削除（実際に移動されたグループのみ）
      const updatedGroups = sourceSheet.groups.filter(g => !groupIdsToDelete.includes(g.id));

      // BP パウチがグループ移動に含まれている場合はクリア
      const bpCleared = sourceSheet.balancingPouchId ? pouchIdsToDelete.includes(sourceSheet.balancingPouchId) : false;

      // 元シートを更新
      const updatedSourceSheet: Sheet = {
        ...sourceSheet,
        pouches: updatedPouches,
        groups: updatedGroups,
        balancingPouchId: bpCleared ? null : sourceSheet.balancingPouchId,
        balancingTargetTotal: bpCleared ? undefined : sourceSheet.balancingTargetTotal,
        // 履歴エントリを追加（シート間移動でのグループ削除）
        history: [
          ...sourceSheet.history,
          {
            timestamp: new Date(),
            groups: updatedGroups,
            pouches: updatedPouches,
            type: HistoryActionType.DELETE_GROUP,
            description: `Move Group to ${state.sheets.find(s => s.id === draggedData.sourceSheetId)?.title || 'Unknown Sheet'}`,
            targetId: groupId
          }
        ],
        historyIndex: sourceSheet.history.length // 新しい履歴エントリを指す
      };

      // 元シートを保存
      await SheetStorage.saveSheet(updatedSourceSheet);
      
      // DB保存の完了を確実に待つ
      await new Promise(resolve => setTimeout(resolve, 500));
      
      
      // 状態を更新
      dispatch({ type: 'UPDATE_SHEET', payload: updatedSourceSheet });
      
      // stateRefも同期的に更新
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s => 
          s.id === updatedSourceSheet.id ? updatedSourceSheet : s
        )
      };

    } catch (error) {
      console.error('元シートからのグループ削除に失敗しました:', error);
    }
  }, [state.sheets, dispatch]);

  // 元シートからパウチを削除する関数
  const removePouchFromSourceSheet = useCallback(async (pouchId: string, sourceSheetId: string) => {
    
    try {
      // 元シートのデータを取得
      const sourceSheet = state.sheets.find(sheet => sheet.id === sourceSheetId);
      if (!sourceSheet) {
        return;
      }


      // 削除されるパウチのDOM要素を確実に削除
      const pouchElement = document.querySelector(`[data-pouch-id="${pouchId}"]`);
      if (pouchElement) {
        
        // イベントリスナーを無効化してから削除
        const clonedElement = pouchElement.cloneNode(true);
        pouchElement.parentNode?.replaceChild(clonedElement, pouchElement);
        
        // 少し遅延してから削除（イベントリスナーが完全に無効化されるまで待つ）
        setTimeout(() => {
          if (clonedElement.parentNode) {
            clonedElement.parentNode.removeChild(clonedElement);
          }
        }, 10);
      }

      // パウチを削除
      const updatedPouches = sourceSheet.pouches.filter(pouch => pouch.id !== pouchId);

      // 元シートを更新
      const updatedSourceSheet: Sheet = {
        ...sourceSheet,
        pouches: updatedPouches,
        // BP パウチが移動された場合はクリア
        balancingPouchId: sourceSheet.balancingPouchId === pouchId ? null : sourceSheet.balancingPouchId,
        balancingTargetTotal: sourceSheet.balancingPouchId === pouchId ? undefined : sourceSheet.balancingTargetTotal,
        // 履歴エントリを追加（シート間移動でのパウチ削除）
        history: [
          ...sourceSheet.history,
          {
            timestamp: new Date(),
            groups: sourceSheet.groups,
            pouches: updatedPouches,
            type: HistoryActionType.DELETE_POUCH,
            description: `Move Pouch to ${state.sheets.find(s => s.id === state.activeSheetId)?.title || 'Unknown Sheet'}`,
            targetId: pouchId
          }
        ],
        historyIndex: sourceSheet.history.length // 新しい履歴エントリを指す
      };


      // 元シートを保存
      await SheetStorage.saveSheet(updatedSourceSheet);
      
      // DB保存の完了を確実に待つ
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 状態を更新
      dispatch({ type: 'UPDATE_SHEET', payload: updatedSourceSheet });
      
      // stateRefも同期的に更新
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s => 
          s.id === updatedSourceSheet.id ? updatedSourceSheet : s
        )
      };
      
      // 元シートがアクティブシートの場合、強制的にリフレッシュしてイベントリスナーをクリーンアップ
      if (sourceSheetId === state.activeSheetId) {
        setTimeout(() => {
          // 強制的にシートを再レンダリング（同じシートIDで再設定）
          dispatch({ type: 'SET_ACTIVE_SHEET', payload: sourceSheetId });
        }, 100);
      }

    } catch (error) {
      console.error('元シートからのパウチ削除に失敗しました:', error);
    }
  }, [state.sheets, dispatch, state.activeSheetId]);

  // デバウンスされたログ出力関数
  const debouncedLog = useDebounce(() => {
  }, 100);

  // タッチデバイス判定時の設定
  useEffect(() => {
    // HTML要素にタッチデバイス用のクラスを追加
    if (state.isTouchDevice) {
      document.documentElement.classList.add('touch-device');
    } else {
      document.documentElement.classList.remove('touch-device');
    }
    
    // HTML要素にモバイル用のクラスを追加
    if (state.isMobile) {
      document.documentElement.classList.add('mobile');
    } else {
      document.documentElement.classList.remove('mobile');
    }
  }, [state.isTouchDevice, state.isMobile]);
  
  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      // すべてのタイマーをクリア
      if (switchTimerRef.current !== null) {
        clearTimeout(switchTimerRef.current);
      }
      if (groupUpdateTimeoutRef.current !== null) {
        clearTimeout(groupUpdateTimeoutRef.current);
      }
      if (pouchUpdateTimeoutRef.current !== null) {
        clearTimeout(pouchUpdateTimeoutRef.current);
      }
      if (historyActionTimerRef.current !== null) {
        clearTimeout(historyActionTimerRef.current);
      }
    };
  }, []);
  
  // アクティブなシートを取得
  const activeSheet = useMemo(() => {
    return state.sheets.find(sheet => sheet.id === state.activeSheetId);
  }, [state.sheets, state.activeSheetId]);
  
  // pouchesの値×マルチの合計を計算
  const pouchesTotal = useMemo(() => {
    if (!activeSheet?.pouches) return 0;
    return activeSheet.pouches.reduce((sum, pouch) => sum + (pouch.value * pouch.multiplier), 0);
  }, [activeSheet?.pouches]);

  // 統計情報更新のuseEffect
  useEffect(() => {
    if (activeSheet) {
      const statistics = calculateStatistics(activeSheet.pouches, activeSheet.groups);
      setStats(statistics);
    }
  }, [pouchesTotal, activeSheet?.pouches, activeSheet?.groups]);
  
  // ActiveSheetComponentへの参照を保持する変数を追加
  const activeSheetRef = useRef<ActiveSheetComponentRef>(null);
  const activeSheetTitleInputRef = useRef<HTMLInputElement>(null);
  const [isActiveSheetTitleFocused, setIsActiveSheetTitleFocused] = useState(false);

  const activeSheetTitleEditTooltipHtml = useMemo(() => {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `${formatTooltip('Enter')} <span class="tt-desc">${esc('Commit Title')}</span><br/>${formatTooltip('Esc')} <span class="tt-desc">${esc('Cancel')}</span>`;
  }, []);
  
  // 前回のhover状態を記録（不要な更新を避けるため）
  const lastHoverStateRef = useRef<{groupId: string | null, pouchId: string | null}>({groupId: null, pouchId: null});
  
  // stateが変更されたらrefも更新（クロージャ問題を回避）
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // アプリケーションの初期化
  useEffect(() => {
    const initApp = async () => {
      try {
        // Service Worker の登録
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.register('/pouches/sw.js');
            
            
            // Service Worker 更新検知
            registration.addEventListener('updatefound', () => {
              
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'activated') {
                    
                    // 新しいService Workerが有効になったらページをリロード
                    window.location.reload();
                  }
                });
              }
            });
          } catch (error) {
            
          }
        }

        // リロード時はアニメーションフラグをON
        setIsInitialLoading(true);
        
        // データベースの初期化
        await initializeData();
        
        // リロード時に全パウチをリセット
        // リロード時はActiveSheetComponentの初期化時に処理されるため、ここでは処理しない
        
        // 初期シートを取得（ストレージから読み込み、または新規作成）
        const initialSheets = await getInitialSheets();
        
        // 各シートのグループパスを修復
        const repairedSheets = initialSheets.map(sheet => {
          const repairedGroups = repairGroupPaths(sheet.groups);
          if (repairedGroups !== sheet.groups) {
            const repairedSheet = { ...sheet, groups: repairedGroups };
            // 修復されたシートをストレージに保存
            SheetStorage.saveSheet(repairedSheet);
            return repairedSheet;
          }
          return sheet;
        });
        
        // 設定の取得
        const settings = await SettingsStorage.getSettings();
        
        // アクティブなシートIDの取得
        const storedActiveSheetId = SheetStorage.getActiveSheetId();
        const activeSheetId = storedActiveSheetId || 
          (repairedSheets.length > 0 ? repairedSheets[0].id : '');
        
        // アプリの状態を初期化
        dispatch({ 
          type: 'INIT_APP', 
          payload: { sheets: repairedSheets, settings } 
        });
        
        // アクティブシートを設定（シートが存在する場合は必ず選択）
        if (repairedSheets.length > 0) {
          // 保存されたactiveSheetIdが有効で、そのシートが存在する場合
          if (storedActiveSheetId && repairedSheets.find(sheet => sheet.id === storedActiveSheetId)) {
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: storedActiveSheetId });
          } else {
            // 保存されたIDが無効または存在しない場合、最新のシート（配列の最初）を選択
            const latestSheet = repairedSheets[0];
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: latestSheet.id });
            // ストレージにも保存
            SheetStorage.saveActiveSheetId(latestSheet.id);
          }
        }
        
        // 設定値からサイドバーの状態を設定
        if (settings.isLeftSidebarOpen !== undefined) {
          dispatch({ type: 'TOGGLE_MENU', payload: settings.isLeftSidebarOpen });
        }
        
        if (settings.isRightSidebarOpen !== undefined) {
          dispatch({ type: 'TOGGLE_SIDEBAR', payload: settings.isRightSidebarOpen });
        }
        
        // 初期化処理完了後、タイマーでフラグをリセット
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 1000);
        
        // ファイルハンドラーとURLパラメータのチェック
        try {
          // Share Target API (POST)からのファイル受信をチェック
          if (window.location.search.includes('files') || document.referrer.includes('share-target')) {
            
            // POSTデータの処理は通常Service Workerで行われるため、
            // ここではログ出力のみ
          }
          
          // URLパラメータをチェック
          const urlParams = new URLSearchParams(window.location.search);
          const importUrl = urlParams.get('import');
          const importFileParam = urlParams.get('import'); // import_file → importに変更
          const importPublicParam = urlParams.get('import_public'); // 公開フォルダからのインポート
          
          // 直接共有URLでアクセスされた場合（例: /pouches/import/filename.pouches）
          const currentPath = window.location.pathname;
          const isDirectImportUrl = currentPath.includes('/import/') && currentPath.endsWith('.pouches');
          
          // importパラメータをチェック（通常のインポート）
          if (importFileParam) {
            
            
            // ファイル名からシート名を推定してタイトルを変更
            const sheetName = importFileParam.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            
            // メタディスクリプションを更新
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetName}"`);
            }
            
            const fullImportUrl = `https://apps.tomippe.jp/pouches/import/${importFileParam}`;
            await importFromServerFile(fullImportUrl);
          }
          // import_publicパラメータをチェック（公開フォルダからのインポート、削除期限なし）
          else if (importPublicParam) {
            
            
            // ファイル名からシート名を推定してタイトルを変更
            const sheetName = importPublicParam.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            
            // メタディスクリプションを更新
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetName}"`);
            }
            
            const fullImportUrl = `https://apps.tomippe.jp/pouches/import_public/${importPublicParam}`;
            await importFromServerFile(fullImportUrl);
          }
          // 直接共有URLでアクセスされた場合をチェック（旧方式、互換性のため残す）
          else if (isDirectImportUrl) {
            
            
            // パスからファイル名を抽出してタイトルを変更
            const fileName = currentPath.split('/').pop() || '';
            const sheetName = fileName.replace('.pouches', '');
            document.title = `${sheetName} - POUCHES`;
            
            // メタディスクリプションを更新
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetName}"`);
            }
            
            const fullImportUrl = `https://apps.tomippe.jp${currentPath}`;
            await importFromServerFile(fullImportUrl);
            
            // URLを/pouches/run/にリダイレクト
            window.history.replaceState(null, '', '/pouches/run/');
          }

          // 通常のimportパラメータもチェック
          else if (importUrl) {
            // URL から .pouches ファイルをダウンロードしてインポート
            const response = await fetch(importUrl);
            if (response.ok) {
              const blob = await response.blob();
              // URLから適切なファイル名を取得
              const urlFileName = importUrl.split('/').pop() || 'imported.pouches';
              const file = new File([blob], urlFileName, { type: 'application/pouches' });
              
              // 既存ウィンドウがあるかチェック
              const existingWindows = await checkForExistingWindows();
              
              if (existingWindows.length > 0) {
                // 既存ウィンドウにファイルを送信
                const success = await sendFileToExistingWindow(file, existingWindows[0]);
                
                // ファイル送信が成功した場合のみウィンドウを閉じる
                if (success) {
                  // ファイルのダブルクリック時のみウィンドウを閉じる
                  // 他の場合は閉じない
                } else {
                  // 送信に失敗した場合は現在のウィンドウで処理
                  await importSheetFromFile(file);
                }
              } else {
                // 新しいウィンドウまたは現在のウィンドウで処理
                await importSheetFromFile(file);
              }
            }
            
            // URLパラメータをクリア
            // window.history.replaceState(null, '', window.location.pathname);
          }
          
          // Web Share Target API からのファイル受信をチェック
          const shareParams = new URLSearchParams(window.location.search);
          if (shareParams.has('file')) {
            try {
              // 共有されたファイルの処理
              
              const fileParam = shareParams.get('file');
              if (fileParam) {
                // 既存ウィンドウがあるかチェック
                const existingWindows = await checkForExistingWindows();
                
                
                
                if (existingWindows.length > 0) {
                  
                  // ファイルパラメータからファイルを取得する処理
                  // 実際の実装はブラウザ依存のため、今後の対応で改善予定
                  
                  // 共有ファイル処理後、新しいウィンドウを閉じる
                  // ファイルのダブルクリック時のみウィンドウを閉じる
                } else {
                  
                  // 新しいウィンドウで処理
                }
              }
            } catch (shareError) {
              
            }
            
            // Share Target URLパラメータをクリア（重複処理防止）
            const url = new URL(window.location.href);
            url.searchParams.delete('file');
            window.history.replaceState(null, '', url.pathname + url.search);
            
          }
          
          // File Handling API をチェック
          
          
          
          if ('launchQueue' in window) {
            
            
            // sessionStorageで永続的な重複防止フラグ（デバッグ時は一時的に無効化）
            const fileHandlerKey = 'pouches_file_handler_processed';
            const hasProcessedFiles = sessionStorage.getItem(fileHandlerKey);
            
            
            
            // デバッグ用：重複防止を一時的に無効化
            const debugMode = false; // 本番では false に変更
            
            if (hasProcessedFiles && !debugMode) {
              
            } else {
              if (debugMode) {
                
              }
              
              (window as any).launchQueue.setConsumer(async (launchParams: any) => {
                
                
                
                
                // ファイルがない場合の処理
                if (!launchParams.files || launchParams.files.length === 0) {
                  
                  
                  // ダブルクリックによる起動かどうかを判定
                  const currentUrl = new URL(window.location.href);
                  const hasFileParams = currentUrl.searchParams.has('import') || 
                                       currentUrl.searchParams.has('file') ||
                                       currentUrl.searchParams.has('url') ||
                                       currentUrl.searchParams.has('import_file') ||
                                       currentUrl.searchParams.has('ms-inputmethod');
                  
                  // リファラーやURLからファイル起動を検出
                  const isFileBasedLaunch = hasFileParams || 
                                           document.referrer.includes('file:') ||
                                           window.location.href !== window.location.origin + '/pouches/run/';
                  
                  
                  
                  
                  
                  
                  if (isFileBasedLaunch) {
                    
                    
                    // Edge PWA でファイルからの起動だが、ファイルにアクセスできない場合
                    setTimeout(() => {
                      if (window.confirm('The app was launched with a file, but the file could not be accessed.\nWould you like to manually select and import the file?')) {
                        // インポートボタンの処理を呼び出し
                        const importEvent = new Event('click');
                        document.querySelector('.import-sheet-button')?.dispatchEvent(importEvent);
                      }
                    }, 1000);
                  } else {
                    
                  }
                  return;
                }
                
                if (launchParams.files && launchParams.files.length > 0) {
                  
                  
                  for (const fileHandle of launchParams.files) {
                    const file = await fileHandle.getFile();
                    if (file.name.endsWith('.pouches')) {
                      
                      // ファイル固有の重複チェック（5秒制限）
                      const fileSpecificKey = `pouches_file_${file.name}_${file.lastModified}_${file.size}`;
                      const lastProcessedTime = sessionStorage.getItem(fileSpecificKey);
                      if (lastProcessedTime && !debugMode) {
                        const currentTime = Date.now();
                        const timeDiff = currentTime - parseInt(lastProcessedTime);
                        if (timeDiff < 5000) { // 5秒以内の重複を防ぐ
                          continue;
                        }
                      }
                      
                      
                      // 既存ウィンドウがあるかチェック
                      const existingWindows = await checkForExistingWindows();
                      
                      
                      if (existingWindows.length > 0) {
                        
                        
                        // ファイル内容を一時的にプレビュー表示（アラート）
                        try {
                          const fileContent = await file.text();
                          const parsedData = JSON.parse(fileContent);
                          const sheetTitle = parsedData.sheets?.[0]?.title || 'Imported Sheet';
                          const pouchCount = parsedData.sheets?.[0]?.pouches?.length || 0;
                          
                          alert(`Importing: ${sheetTitle}\nPouches: ${pouchCount}`);
                        } catch (error) {
                          alert(`Importing: ${file.name}`);
                        }
                        
                        // 既存ウィンドウにファイルを送信（リーダー選出システムで処理）
                        
                        const success = await sendFileToExistingWindow(file, existingWindows[0]);
                        
                        // 送信完了後にウィンドウを閉じる（ファイルのダブルクリック時のみ）
                        setTimeout(() => {
                          window.close();
                        }, 300); // 0.5秒後に閉じる
                        
                      } else {
                        
                        // 新しいウィンドウまたは現在のウィンドウで処理
                        await importSheetFromFile(file, false); // 重複チェックあり
                      }
                      
                      // ファイル処理完了をマーク
                      sessionStorage.setItem(fileSpecificKey, Date.now().toString());
                      
                      
                      break; // 最初のファイルのみ処理
                    }
                  }
                }
              });
            }
          } else {
            
            
            // Edge PWA などでFile Handling APIが利用できない場合の代替処理
            // URLパラメータやクエリストリングをチェック
            const currentUrl = window.location.href;
            
            
            // Edge PWA でファイルが渡される可能性のあるパターンをチェック
            if (currentUrl.includes('source-ms-file://') || currentUrl.includes('file://')) {
              
              // この場合は手動でファイル処理が必要
            }
          }
        } catch (error) {
          console.error('ファイルハンドラーからのインポートに失敗しました:', error);
        }
        
      } catch (error) {
        console.error('アプリケーションの初期化に失敗しました:', error);
        // エラー時もフラグをリセット
        setIsInitialLoading(false);
      }
    };
    
    initApp();
  }, []);
  
  // サーバーからファイルを取得してインポートする関数
  const importFromServerFile = useCallback(async (serverFilePath: string, skipConfirm: boolean = false, skipWindowClose: boolean = false, skipBroadcast: boolean = false) => {
    try {
      
      const response = await fetch(serverFilePath);
      
      if (response.ok) {
        const jsonData = await response.text();
        const parsedData = JSON.parse(jsonData);
        
        // DB内のタイトルを使用（ファイル名ではなく）
        const sheetTitle = parsedData.sheets?.[0]?.title || 'Imported Sheet';
        
        // タイトルとディスクリプションを実際のシート名で更新
        document.title = `${sheetTitle} - POUCHES`;
        
        // メタディスクリプションを更新
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetTitle}"`);
        }
        
        const file = new File([jsonData], `${sheetTitle}.pouches`, { type: 'application/pouches' });

        // インポート確認ダイアログ（importボタンからの場合はスキップ）
        if (skipConfirm) {
          
          await executeImport(file, serverFilePath, skipWindowClose, skipBroadcast);
        } else {
          showCustomConfirm(sheetTitle, async () => {
            
            await executeImport(file, serverFilePath, skipWindowClose, skipBroadcast);
          });
        }
      } else {
        console.error('[Protocol Import] サーバーファイルの取得に失敗:', response.statusText);
        alert('Import Failed');
      }
    } catch (error) {
      console.error('[Protocol Import] エラー:', error);
      alert('Import Failed');
    }
  }, []);
  
  // URLパラメータの変更を監視（エアドロップ等の連続起動対応）
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const importUrl = urlParams.get('import');
      const importFileParam = urlParams.get('import');
      const importPublicParam = urlParams.get('import_public');
      
      // インポートパラメータがある場合のみ処理
      if (importFileParam || importPublicParam) {
        
        // 非同期でインポート処理を実行
        const processImport = async () => {
          try {
            if (importFileParam) {
              const sheetName = importFileParam.replace('.pouches', '');
              document.title = `${sheetName} - POUCHES`;
              
              const metaDescription = document.querySelector('meta[name="description"]');
              if (metaDescription) {
                metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetName}"`);
              }
              
              const fullImportUrl = `https://apps.tomippe.jp/pouches/import/${importFileParam}`;
              await importFromServerFile(fullImportUrl);
            } else if (importPublicParam) {
              const sheetName = importPublicParam.replace('.pouches', '');
              document.title = `${sheetName} - POUCHES`;
              
              const metaDescription = document.querySelector('meta[name="description"]');
              if (metaDescription) {
                metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetName}"`);
              }
              
              const fullImportUrl = `https://apps.tomippe.jp/pouches/import_public/${importPublicParam}`;
              await importFromServerFile(fullImportUrl);
            }
            
            // URLパラメータをクリア
            const url = new URL(window.location.href);
            url.searchParams.delete('import');
            url.searchParams.delete('import_public');
            window.history.replaceState(null, '', url.pathname + url.search);
            
          } catch (error) {
            console.error('[URL Change] Failed to process import:', error);
          }
        };
        
        processImport();
      }
    };
    
    // 初回チェック
    handleUrlChange();
    
    // popstateイベント（ブラウザの戻る/進む）を監視
    window.addEventListener('popstate', handleUrlChange);
    
    // 定期的にURLパラメータをチェック（エアドロップ等の外部からの起動対応）
    const intervalId = setInterval(handleUrlChange, 1000);
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      clearInterval(intervalId);
    };
  }, [importFromServerFile]);
  
  // ウィンドウリサイズ時の処理
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      
      // モバイル状態が変わった場合のみ更新
      if (mobile !== state.isMobile) {
        // isMobileの状態を更新
        dispatch({ type: 'SET_MOBILE_STATE', payload: mobile });
        
        // モバイル状態になった場合は、メニューとサイドバーを閉じる
        if (mobile) {
          dispatch({ type: 'TOGGLE_MENU', payload: false });
          dispatch({ type: 'TOGGLE_SIDEBAR', payload: false });
          
          // 設定も更新
          SettingsStorage.saveSettings({
            ...state.settings,
            isLeftSidebarOpen: false,
            isRightSidebarOpen: false
          });
        } else {
          // PCに戻った場合は、設定に保存されている状態を復元
          dispatch({ type: 'TOGGLE_MENU', payload: state.settings.isLeftSidebarOpen });
          dispatch({ type: 'TOGGLE_SIDEBAR', payload: state.settings.isRightSidebarOpen });
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [state.isMobile, state.settings]);
  
  // メニューとサイドバーの切り替え
  const toggleMenu = (e?: React.MouseEvent | React.TouchEvent) => {
    dispatch({ type: 'TOGGLE_MENU' });
    
    // 設定を保存
    const newIsLeftSidebarOpen = !state.isLeftSidebarOpen;
    SettingsStorage.saveSettings({
      ...state.settings,
      isLeftSidebarOpen: newIsLeftSidebarOpen
    });
  };
  
  const toggleSidebar = (e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }
    dispatch({ type: 'TOGGLE_SIDEBAR' });
    
    // 設定を保存
    const newIsRightSidebarOpen = !state.isRightSidebarOpen;
    SettingsStorage.saveSettings({
      ...state.settings,
      isRightSidebarOpen: newIsRightSidebarOpen
    });
  };
  
  // シートの新規作成
  const handleCreateNewSheet = async (e?: React.MouseEvent | React.TouchEvent) => {
    // モバイルの場合は左サイドバーを閉じる
    if (state.isMobile && state.isLeftSidebarOpen) {
      dispatch({ type: 'TOGGLE_MENU', payload: false });
    }
    
    try {
      // 事前読み込み方式で新しいシート（初期パウチ付き）を作成
      const newSheet = await createAndSaveNewSheet(state.sheets.length);
      
      // Reducerに新しいシートを追加
      dispatch({ type: 'ADD_SHEET', payload: newSheet });
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: newSheet.id });
      
      // タイトル入力にフォーカスを当てて文字列を選択状態にする
      setTimeout(() => {
        const titleInput = document.querySelector(
          `.sheet-item[data-sheet-id="${newSheet.id}"] input`
        ) as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select(); // 文字列を選択状態にする
        }
      }, 100);
      
    } catch (error) {
      console.error('新規シート作成でエラーが発生しました:', error);
    }
  };
  
  // シートの選択
  const handleSelectSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
      
      // input要素内のタップでは処理しない
      const target = e.target as HTMLElement;
      if (target.tagName.toLowerCase() === 'input') {
        return;
      }
    }

    // 全ての削除ボタンからconfirmingクラスを削除
    document.querySelectorAll('.delete-sheet-button').forEach(button => {
      button.classList.remove('confirming');
    });
    
    // シート間移動中でない場合のみ、全てのインタラクション状態をリセット
    if (!isDraggingToSheet) {
      
      // グローバルインタラクション状態をクリア
      if (globalInteractionRef.current) {
        globalInteractionRef.current = null;
      }
      
      // 現在のActiveSheetComponentのインタラクション状態をリセット
      if (activeSheetRef.current) {
        const currentComponent = activeSheetRef.current as any;
        if (currentComponent.resetInteraction) {
          currentComponent.resetInteraction();
        }
      }
      
      // ドラッグ関連の参照をクリア
      draggedGroupDataRef.current = null;
      draggedPouchDataRef.current = null;
      sourceSheetGroupRef.current = null;
      sourceSheetPouchRef.current = null;
      
      // 保存されたドラッグ状態もクリア
      preservedDragStateRef.current = null;
      
      // ホバータイマーもクリア
      if (sheetHoverTimerRef.current) {
        clearTimeout(sheetHoverTimerRef.current);
        sheetHoverTimerRef.current = null;
      }
      setHoveredSheetId(null);
      
    } else {
    }
    
    await selectSheet(sheetId, dispatch);
  };
  
  // シートタイトルの編集
  const handleSheetTitleChange = async (sheetId: string, newTitle: string) => {
    await updateSheetTitle(sheetId, newTitle, state.sheets, dispatch);
    
    // stateRefも同期的に更新（setTimeout内のクロージャが古いデータを参照する問題を防ぐ）
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s => 
        s.id === sheetId ? { ...s, title: newTitle, lastModified: new Date() } : s
      )
    };
  };
  
  // シートの削除
  const handleDeleteSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }

    // クリックされた削除ボタンを取得
    const target = e?.target as HTMLElement;
    const deleteButton = target.closest('.delete-sheet-button') as HTMLButtonElement;
    
    // Shiftキーが押されている場合はシートを選択してから削除処理を実行
    if (e && 'shiftKey' in e && e.shiftKey) {
      const targetSheet = state.sheets.find(sheet => sheet.id === sheetId);
      if (!targetSheet) return;

      try {
        // まずそのシートを選択する
        await handleSelectSheet(sheetId, e);
        
        // シート選択後に削除処理を実行
        // 現在の状態を保存（アニメーション用）
        const currentPouches = targetSheet.pouches;
        const currentGroups = targetSheet.groups;
        
        // 空の状態を作成
        const emptyPouches: Pouch[] = [];
        const emptyGroups: Group[] = [];
        
        // applyAnimationFlagsを使用してアニメーション用フラグを設定
        const animationResult = HistoryLogic.applyAnimationFlags(
          currentPouches,
          emptyPouches,
          currentGroups,
          emptyGroups
        );
        
        // アクティブシートの場合は即座にアニメーション状態を反映
        if (activeSheetRef.current) {
          activeSheetRef.current.setPouches(animationResult.pouches);
          activeSheetRef.current.setGroups(animationResult.groups);
        }
        
        // 300ms後に最終状態に更新
        setTimeout(async () => {
          // 履歴の処理：削除前の履歴とクリア履歴のみ残す
          const currentHistory = targetSheet.history || [];
          const currentIndex = targetSheet.historyIndex || 0;
          
          // 削除前の履歴（現在の履歴エントリ）を保存
          const beforeDeleteEntry = currentHistory[currentIndex];
          
          // 新しい履歴エントリを作成（クリア後の状態）
          const clearEntry: SheetHistoryEntry = {
            timestamp: new Date(),
            groups: [],
            pouches: [],
            description: 'All Clear',
            type: HistoryActionType.DELETE_GROUP,
            targetId: sheetId
          };
          
          // 履歴を再構築：削除前の履歴 + クリア履歴
          const newHistory = beforeDeleteEntry 
            ? [beforeDeleteEntry, clearEntry]
            : [clearEntry];
          
          // シート内の全グループとパウチを削除
          const updatedSheet = {
            ...targetSheet,
            pouches: [],
            groups: [],
            history: newHistory,
            historyIndex: newHistory.length - 1, // 最後のエントリ（クリア履歴）を指す
            lastModified: new Date()
          };

          // シートを更新
          dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
          
          // stateRefも同期的に更新
          stateRef.current = {
            ...stateRef.current,
            sheets: stateRef.current.sheets.map(s => 
              s.id === updatedSheet.id ? updatedSheet : s
            )
          };

          // 永続化
          await SheetStorage.saveSheet(updatedSheet);

          // 統計情報をリセット
          setStats({
            count: 0,
            total: 0,
            average: 0,
            max: 0,
            min: 0
          });
        }, 300);
      } catch (error) {
        console.error('シート内のグループとパウチの削除に失敗しました:', error);
      }
      return;
    }

    // confirmingクラスがついている場合は削除を実行
    if (deleteButton?.classList.contains('confirming')) {
      try {
        await deleteSheet(sheetId, state.sheets, dispatch);
        
        // 最新のシート数を取得
        const currentSheets = state.sheets.filter(sheet => sheet.id !== sheetId);
        
        // シートがなくなった場合は初期化シートを作成
        if (currentSheets.length === 0) {
          try {
            // 事前読み込み方式で初期化シート（初期パウチ付き）を作成
            const initSheet = await createAndSaveInitializationSheet();
            
            // Reducerに初期化シートを追加
            dispatch({ type: 'ADD_SHEET', payload: initSheet });
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: initSheet.id });
            
            // 統計情報をリセット（前の合計が残らないようにする）
            setStats({
              count: 0,
              total: 0,
              average: 0,
              max: 0,
              min: 0
            });
            
            return;
          } catch (error) {
            console.error('初期化シート作成でエラーが発生しました:', error);
            return;
          }
        }
        
        // シート数が1になった場合の自動パウチ作成処理を削除
        // 最後のシートを削除して初期シートが作られた場合のみパウチを作成するため
      } catch (error) {
        console.error('シートの削除に失敗しました:', error);
      }
      return;
    }

    // confirmingクラスを付与
    if (deleteButton) {
      deleteButton.classList.add('confirming');
    } else {
      console.error('削除ボタンが見つかりません');
    }
    setTimeout(() => {
      deleteButton?.classList.remove('confirming');
    }, 2000);
  };
  
  // シートの複製
  const handleDuplicateSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }

    // 複製元のシートを取得
    const sourceSheet = state.sheets.find(sheet => sheet.id === sheetId);
    if (!sourceSheet) return;

    try {
      // 新しいシートIDを生成
      const newSheetId = uuidv4();

      // 複製したシートを作成（履歴とスナップショットは除外）
      const duplicatedSheet: Sheet = {
        id: newSheetId,
        title: `${sourceSheet.title} - copy`, // タイトルに「- copy」を追加
        pouches: sourceSheet.pouches.map(pouch => ({
          ...pouch,
          id: uuidv4() // 新しいIDを生成
        })),
        groups: sourceSheet.groups.map(group => ({
          ...group,
          id: uuidv4(), // 新しいIDを生成
          pouches: [] // パウチIDは後で更新
        })),
        createdAt: new Date(),
        lastModified: new Date(),
        lastPouchValue: sourceSheet.lastPouchValue,
        lastPouchColor: sourceSheet.lastPouchColor,
        lastGroupColor: sourceSheet.lastGroupColor,
        colorLegends: sourceSheet.colorLegends,
        history: [], // 履歴は新規作成
        historyIndex: 0,
        snapshots: [] // スナップショットは新規作成
      };

      // グループ内のパウチIDを更新
      const pouchIdMap = new Map(
        sourceSheet.pouches.map((oldPouch, index) => [
          oldPouch.id,
          duplicatedSheet.pouches[index].id
        ])
      );

      // グループIDのマッピングを作成
      const groupIdMap = new Map(
        sourceSheet.groups.map((oldGroup, index) => [
          oldGroup.id,
          duplicatedSheet.groups[index].id
        ])
      );

      duplicatedSheet.groups = duplicatedSheet.groups.map(group => ({
        ...group,
        pouches: group.pouches.map(oldPouchId => pouchIdMap.get(oldPouchId) || oldPouchId)
      }));

      // スナップショット内のIDも新しいものに更新
      duplicatedSheet.snapshots = (sourceSheet.snapshots || []).map(snapshot => ({
        ...snapshot,
        id: uuidv4(), // スナップショットIDも新規生成
        pouches: snapshot.pouches.map(pouch => ({
          ...pouch,
          id: pouchIdMap.get(pouch.id) || uuidv4(),
          referencedGroupId: pouch.isGroupTotalPouch && pouch.referencedGroupId
            ? groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId
            : pouch.referencedGroupId
        })),
        groups: snapshot.groups.map(group => ({
          ...group,
          id: groupIdMap.get(group.id) || uuidv4(),
          pouches: (group.pouches || []).map(oldPouchId => pouchIdMap.get(oldPouchId) || oldPouchId)
        }))
      }));

      // GTPのreferencedGroupIdを更新
      duplicatedSheet.pouches = duplicatedSheet.pouches.map(pouch => {
        if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
          return {
            ...pouch,
            referencedGroupId: groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId
          };
        }
        return pouch;
      });

      // シートを追加
      dispatch({ type: 'ADD_SHEET', payload: duplicatedSheet });
      
      // アクティブシートを変更
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: duplicatedSheet.id });

      // 永続化
      await SheetStorage.saveSheet(duplicatedSheet);
      await SheetStorage.saveActiveSheetId(duplicatedSheet.id);

      // タイトル入力にフォーカスを当てて文字列を選択状態にする
      setTimeout(() => {
        const titleInput = document.querySelector(
          `.sheet-item[data-sheet-id="${duplicatedSheet.id}"] input`
        ) as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select(); // 文字列を選択状態にする
        }
      }, 100);

    } catch (error) {
      console.error('シートの複製に失敗しました:', error);
    }
  };

  // 共有メニューを表示する関数
  const showShareMenu = (httpsUrl: string, title: string) => {
    if (navigator.share) {
      // Web Share API では互換性重視でHTTPS URLを使用（PWAスコープ内なのでPWAで開く）
      navigator.share({
        // title: `POUCHES: ${title}`,
        // text: `POUCHES: ${title}`,
        url: httpsUrl
      }).catch((error) => {
        // Web Share API失敗時はURLのみをクリップボードにコピー
        copyToClipboard(httpsUrl);
      });
      
      // PCの場合、同時にHTTPS URLをクリップボードにコピー
      if (!navigator.userAgent.match(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i)) {
        copyToClipboard(httpsUrl);
      }
    } else {
      // フォールバック: URLのみをクリップボードにコピー
      copyToClipboard(httpsUrl);
    }
  };

  // シンプルなクリップボードコピー関数
  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // フォールバック: execCommand
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch (error) {
      
    }
  };
  // サーバーにファイルをアップロードする関数
  const uploadSheetToServer = async (jsonData: string, fileName: string, isPublic: boolean = false): Promise<string | null> => {
    try {
      // シリアライズされたファイル名を生成（タイムスタンプ + ランダム文字列）
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 15);
      const serializedFileName = `${timestamp}_${randomStr}.pouches`;
      
      // FormDataを作成
      const formData = new FormData();
      const blob = new Blob([jsonData], { type: 'application/pouches' });
      
      // ファイルサイズをログ出力
      const fileSizeMB = (blob.size / 1024 / 1024).toFixed(2);
      console.log(`アップロードファイルサイズ: ${fileSizeMB} MB (${blob.size} bytes)`);
      
      formData.append('file', blob, serializedFileName);
      formData.append('originalFileName', fileName);
      formData.append('isPublic', isPublic.toString());
      
      // サーバーにアップロード（Electron では絶対URLを使用）
      const apiUrl = (window as unknown as { electron?: unknown }).electron
        ? 'https://apps.tomippe.jp/pouches/api/upload-temp.php'
        : '/pouches/api/upload-temp.php';
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.fileName || serializedFileName;
      } else {
        // エラーレスポンスの詳細を取得
        const errorText = await response.text();
        console.error('サーバーアップロードに失敗:', response.statusText);
        console.error('エラー詳細:', errorText);
        return null;
      }
    } catch (error) {
      console.error('サーバーアップロードでエラー:', error);
      return null;
    }
  };

  // エクスポートアクション実行（download / private / public）
  const executeExportAction = useCallback(async (sheetId: string, action: 'download' | 'private' | 'public', existingFilePath?: string) => {
    const targetSheet = state.sheets.find(sheet => sheet.id === sheetId);
    if (!targetSheet) return;
    const fileName = `${targetSheet.title || 'No Title'}.pouches`;

    if (action === 'download') {
      const exportData = { sheets: [targetSheet] };
      const jsonData = JSON.stringify(exportData, null, 2);
      const electron = (window as unknown as { electron?: { showSaveDialog: (n?: string) => Promise<{ canceled: boolean; filePath?: string }>; writeFile: (p: string, c: string) => Promise<{ success: boolean }> } }).electron;
      if (electron?.showSaveDialog && electron?.writeFile) {
        let pathToUse = existingFilePath;
        if (!pathToUse) {
          const { canceled, filePath } = await electron.showSaveDialog(fileName);
          pathToUse = filePath;
        }
        if (pathToUse) {
          const result = await electron.writeFile(pathToUse, jsonData);
          if (result.success) activeSheetFilePathRef.current.set(sheetId, pathToUse);
          else alert('Save failed.');
        }
      } else {
        const blob = new Blob([jsonData], { type: 'application/pouches' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } else {
      const isPublic = action === 'public';
      const sheetWithoutHistory = { ...targetSheet, history: [], historyIndex: 0 };
      const exportData = { sheets: [sheetWithoutHistory] };
      const jsonData = JSON.stringify(exportData, null, 2);
      let serializedFileName = await uploadSheetToServer(jsonData, fileName, isPublic);
      if (!serializedFileName) {
        serializedFileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.pouches`;
      }
      if (serializedFileName) {
        const urlParam = isPublic ? 'import_public' : 'import';
        showShareMenu(`https://apps.tomippe.jp/pouches/run/?${urlParam}=${serializedFileName}`, targetSheet.title || 'No Title');
      }
    }
  }, [state.sheets, uploadSheetToServer, showShareMenu]);

  // シートのエクスポート
  const handleExportSheet = async (sheetId: string, e?: React.MouseEvent | React.TouchEvent, directAction?: 'download' | 'private' | 'public') => {
    if (e && 'touches' in e) e.preventDefault();
    const targetSheet = state.sheets.find(sheet => sheet.id === sheetId);
    if (!targetSheet) return;
    try {
      if (directAction) {
        await executeExportAction(sheetId, directAction);
      } else {
        showCustomExportConfirm(targetSheet.title || 'No Title', async (action) => {
          await executeExportAction(sheetId, action);
        });
      }
    } catch (error) {
      console.error('Export failed:', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  // ファイルからシートをインポートする共通処理
  const importSheetFromFile = useCallback(async (file: File, skipDuplicateCheck = false, skipBroadcast = false, filePath?: string) => {
    const importId = `IMPORT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    
    try {
      
      
      // ファイルの内容を読み取り
      const text = await file.text();
      
      
      // 重複チェック（BroadcastChannel経由の場合のみ）
      if (!skipDuplicateCheck) {
        
        // ファイル内容のハッシュ生成（簡易版）
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
          const char = text.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // 32bit整数に変換
        }
        
        // 重複インポート防止：ファイル内容ベースで判定
        const contentKey = `import_content_${hash}`;
        const fileKey = `import_${file.name}_${file.size}_${file.lastModified}`;
        const lastImportTime = sessionStorage.getItem(contentKey) || sessionStorage.getItem(fileKey);
        const currentTime = Date.now();
        
        
        
        
        
        
        
        if (lastImportTime && (currentTime - parseInt(lastImportTime)) < 5000) { // 5秒の重複防止
          const timeDiff = currentTime - parseInt(lastImportTime);
          return;
        }
        
        // インポート時刻を記録（両方のキーで）
        sessionStorage.setItem(contentKey, currentTime.toString());
        sessionStorage.setItem(fileKey, currentTime.toString());
        
        
      }
      
      // 既に読み取り済みのテキストを使用
      const importData = JSON.parse(text);

      // データ構造の検証
      if (!importData.sheets || !Array.isArray(importData.sheets) || importData.sheets.length === 0) {
        console.error(`[${importId}] 無効なファイル形式`);
        alert('無効なファイル形式です。正しい.pouchesファイルを選択してください。');
        return;
      }

      // 最初のシートを取得
      const sourceSheet = importData.sheets[0];
      
      
      // ファイル名から拡張子を除いたシート名を取得
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
      
      // 新しいシートIDを生成
      const newSheetId = uuidv4();

      // インポートしたシートを作成（新しいIDで）
      const importedSheet: Sheet = {
        ...sourceSheet,
        id: newSheetId,
        title: fileNameWithoutExtension, // ファイル名（拡張子除く）をシート名に設定
        pouches: sourceSheet.pouches.map((pouch: any) => ({
          ...pouch,
          id: uuidv4() // 新しいIDを生成
        })),
        groups: sourceSheet.groups.map((group: any) => ({
          ...group,
          id: uuidv4(), // 新しいIDを生成
          pouches: [] // パウチIDは後で更新
        })),
        createdAt: new Date(),
        lastModified: new Date(),
        history: [], // 後で更新
        historyIndex: 0,
        snapshots: [] // 後で更新
      };

      // グループ内のパウチIDを更新
      const pouchIdMap = new Map(
        sourceSheet.pouches.map((oldPouch: any, index: number) => [
          oldPouch.id,
          importedSheet.pouches[index].id
        ])
      );

      const groupIdMap = new Map<string, string>(
        sourceSheet.groups.map((oldGroup: any, index: number) => [
          oldGroup.id,
          importedSheet.groups[index].id
        ])
      );

      importedSheet.groups = importedSheet.groups.map((group, index) => ({
        ...group,
        pouches: (sourceSheet.groups[index].pouches || []).map((oldPouchId: string) => pouchIdMap.get(oldPouchId) || oldPouchId)
      }));

      // GTPのreferencedGroupIdを更新
      importedSheet.pouches = importedSheet.pouches.map((pouch): Pouch => {
        if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
          const newReferencedGroupId: string | undefined = groupIdMap.get(pouch.referencedGroupId);
          return {
            ...pouch,
            referencedGroupId: newReferencedGroupId ? newReferencedGroupId : pouch.referencedGroupId
          };
        }
        return pouch;
      });

      // 履歴内のIDも新しいものに更新
      importedSheet.history = (sourceSheet.history || []).map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp),
        pouches: entry.pouches.map((pouch: any) => {
          const newPouchId = pouchIdMap.get(pouch.id);
          const newReferencedGroupId = pouch.isGroupTotalPouch && pouch.referencedGroupId
            ? (groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId)
            : pouch.referencedGroupId;
          return {
            ...pouch,
            id: newPouchId ? newPouchId : pouch.id,
            referencedGroupId: newReferencedGroupId
          };
        }),
        groups: entry.groups.map((group: any) => ({
          ...group,
          id: groupIdMap.get(group.id) || group.id,
          pouches: (group.pouches || []).map((oldPouchId: string) => pouchIdMap.get(oldPouchId) || oldPouchId)
        }))
      }));
      
      // historyIndexも保持
      importedSheet.historyIndex = Math.min(sourceSheet.historyIndex || 0, importedSheet.history.length - 1);
      if (importedSheet.historyIndex < 0 && importedSheet.history.length > 0) {
        importedSheet.historyIndex = 0;
      }

      // スナップショット内のIDも新しいものに更新
      importedSheet.snapshots = (sourceSheet.snapshots || []).map((snapshot: any) => ({
        ...snapshot,
        id: uuidv4(), // スナップショットIDも新規生成
        timestamp: new Date(snapshot.timestamp),
        pouches: snapshot.pouches.map((pouch: any) => {
          const newPouchId = pouchIdMap.get(pouch.id);
          const newReferencedGroupId = pouch.isGroupTotalPouch && pouch.referencedGroupId
            ? (groupIdMap.get(pouch.referencedGroupId) || pouch.referencedGroupId)
            : pouch.referencedGroupId;
          return {
            ...pouch,
            id: newPouchId ? newPouchId : uuidv4(),
            referencedGroupId: newReferencedGroupId
          };
        }),
        groups: snapshot.groups.map((group: any) => ({
          ...group,
          id: groupIdMap.get(group.id) || uuidv4(),
          pouches: (group.pouches || []).map((oldPouchId: string) => pouchIdMap.get(oldPouchId) || oldPouchId)
        }))
      }));

      // シートを追加
      
      dispatch({ type: 'ADD_SHEET', payload: importedSheet });
      
      // アクティブシートを変更
      
      dispatch({ type: 'SET_ACTIVE_SHEET', payload: importedSheet.id });

      if (filePath) activeSheetFilePathRef.current.set(importedSheet.id, filePath);

      // 永続化
      
      await SheetStorage.saveSheet(importedSheet);
      
      await SheetStorage.saveActiveSheetId(importedSheet.id);
      

      // タイトル入力にフォーカスを当てて全選択
      setTimeout(() => {
        const titleInput = document.querySelector(
          `.sheet-item[data-sheet-id="${importedSheet.id}"] input`
        ) as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select(); // テキストを全選択
        }
      }, 100);

      

    } catch (error) {
      console.error(`[${importId}] importSheetFromFileエラー:`, error);
      console.error('ファイルの読み込みに失敗しました:', error);
      alert('Import failed. Please select a valid .pouches file.');
    }
  }, []);

  // インポート実行関数（重複実行を防ぐため外部に定義）
  const executeImport = useCallback(async (file: File, serverFilePath: string, skipWindowClose: boolean = false, skipBroadcast: boolean = false) => {
    
    
    // より厳密な重複実行防止のチェック（ファイル内容も含む）
    const fileContent = await file.text();
    const contentHash = fileContent.substring(0, 100); // 最初の100文字でハッシュ的にチェック
    const importKey = `${file.name}_${file.lastModified}_${file.size}_${contentHash.length}`;
    const existingImport = sessionStorage.getItem(`importing_${importKey}`);
    if (existingImport) {
      
      return;
    }
    
    // インポート開始フラグを設定
    sessionStorage.setItem(`importing_${importKey}`, Date.now().toString());
    
    
    // インポートボタンからの実行時はBroadcastChannel通知を抑制するフラグを設定
    if (skipBroadcast) {
      
      sessionStorage.setItem('skip_broadcast_refresh', 'true');
    }
    
    try {
      // 既に読み込んだファイル内容から新しいFileオブジェクトを作成
      const processFile = new File([fileContent], file.name, { type: file.type, lastModified: file.lastModified });
      
      // 既存ウィンドウがあるかチェック
      const existingWindows = await checkForExistingWindows();
      
      if (existingWindows.length > 0) {
        // 既存ウィンドウにファイルを送信
        const success = await sendFileToExistingWindow(processFile, existingWindows[0]);
        
        if (success) {
          // インポート成功後、Private Shareの場合のみ15分後にサーバーファイルを削除
          // Public Share（import_public）の場合は削除しない
          if (!serverFilePath.includes('/import_public/')) {
            setTimeout(async () => {
              
              await fetch(serverFilePath.replace('/import/', '/api/delete-temp.php?filename=').replace('https://apps.tomippe.jp/pouches/', '/pouches/'), {
                method: 'GET'
              });
            }, 900000); // 15分 = 900,000ms
          }
          
          // ファイルのダブルクリック時のみウィンドウを閉じる
          // 他の場合は閉じない
        } else {
          // 送信に失敗した場合は現在のウィンドウで処理
          await importSheetFromFile(processFile, true, skipBroadcast); // 重複チェックとBroadcast通知をスキップ
          
          // インポート成功後、Private Shareの場合のみ15分後にサーバーファイルを削除
          // Public Share（import_public）の場合は削除しない
          if (!serverFilePath.includes('/import_public/')) {
            setTimeout(async () => {
              
              await fetch(serverFilePath.replace('/import/', '/api/delete-temp.php?filename=').replace('https://apps.tomippe.jp/pouches/', '/pouches/'), {
                method: 'GET'
              });
            }, 900000); // 15分 = 900,000ms
          }
        }
      } else {
        // 新しいウィンドウまたは現在のウィンドウで処理
        await importSheetFromFile(processFile, true, skipBroadcast); // 重複チェックとBroadcast通知をスキップ
        
        // インポート成功後、Private Shareの場合のみ15分後にサーバーファイルを削除
        // Public Share（import_public）の場合は削除しない
        if (!serverFilePath.includes('/import_public/')) {
          setTimeout(async () => {
            
            await fetch(serverFilePath.replace('/import/', '/api/delete-temp.php?filename=').replace('https://apps.tomippe.jp/pouches/', '/pouches/'), {
              method: 'GET'
            });
          }, 900000); // 15分 = 900,000ms
        }
      }
    } finally {
      // インポート完了後にフラグを削除
      sessionStorage.removeItem(`importing_${importKey}`);
      if (skipBroadcast) {
        sessionStorage.removeItem('skip_broadcast_refresh');
        
      }
      
    }
  }, []);


  // インポートコードまたはURLからインポートする関数
  const handleImportFromCode = async (input: string) => {
    try {
      let fileName = '';
      
      // フルURLの場合（https://apps.tomippe.jp/pouches/run/?import=xxx.pouches）
      let isPublicImport = false;
      if (input.startsWith('https://') || input.startsWith('http://')) {
        const url = new URL(input);
        const importFileParam = url.searchParams.get('import');
        const importPublicParam = url.searchParams.get('import_public');
        if (importFileParam) {
          fileName = importFileParam;
        } else if (importPublicParam) {
          fileName = importPublicParam;
          isPublicImport = true;
        } else {
          alert('Invalid URL format. Please use the correct import URL.');
          return;
        }
      } 
      // インポートコードのみの場合（xxx.pouches または xxx）
      else {
        fileName = input.endsWith('.pouches') ? input : `${input}.pouches`;
      }
      
      
      
      // ファイル名からシート名を推定してタイトルを変更
      const sheetName = fileName.replace('.pouches', '');
      document.title = `${sheetName} - POUCHES`;
      
      // メタディスクリプションを更新
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Import POUCHES sheet: "${sheetName}"`);
      }
      
      // サーバーファイルのフルパスを構築（import_publicパラメータがある場合は公開フォルダから）
      const fullImportUrl = isPublicImport 
        ? `https://apps.tomippe.jp/pouches/import_public/${fileName}`
        : `https://apps.tomippe.jp/pouches/import/${fileName}`;
      
      // サーバーからファイルを取得してインポート（importボタンからは確認スキップ、ウィンドウクローズもスキップ、Broadcast通知もスキップ）
      await importFromServerFile(fullImportUrl, true, true, true);
      
    } catch (error) {
      console.error('[Import Code] エラー:', error);
      alert('Import Failed');
    }
  };

  // カスタムプロンプトの状態管理
  const [showImportPrompt, setShowImportPrompt] = useState(false);
  const [importInputValue, setImportInputValue] = useState('');
  
  // カスタム確認ダイアログの状態管理
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [confirmSheetTitle, setConfirmSheetTitle] = useState('');
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);

  // エクスポート確認ダイアログの状態管理
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [exportSheetTitle, setExportSheetTitle] = useState('');
  const [exportCallback, setExportCallback] = useState<((action: 'download' | 'private' | 'public') => void) | null>(null);

  // シートのインポート（ボタンクリック）
  const handleImportSheet = async (e?: React.MouseEvent | React.TouchEvent) => {
    // タッチイベントの場合はpreventDefaultを実行
    if (e && 'touches' in e) {
      e.preventDefault();
    }

    // PWA/Electron 共通：カスタムプロンプトを表示（URL入力 or ファイル選択）
    setImportInputValue('');
    setShowImportPrompt(true);
  };

  // カスタムプロンプトでOKが押された時の処理
  const handleImportPromptOK = async () => {
    setShowImportPrompt(false);

    if (importInputValue.trim() === '') {
      // 空の場合はファイルダイアログを開く
      const electron = (window as unknown as { electron?: { showOpenDialog: () => Promise<{ canceled: boolean; filePaths: string[] }>; readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }> } }).electron;
      if (electron?.showOpenDialog) {
        try {
          const { canceled, filePaths } = await electron.showOpenDialog();
          if (!canceled && filePaths?.[0]) {
            const result = await electron.readFile(filePaths[0]);
            if (result.success && result.content) {
              const file = new File([result.content], result.fileName || 'imported.pouches', { type: 'application/pouches' });
              await handleUnifiedFileImport(file, true, filePaths[0]);
            } else {
              alert('Failed to read file.');
            }
          }
        } catch (err) {
          console.error('Import failed:', err);
          alert('Import failed.');
        }
      } else {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pouches';
        input.onchange = async (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) return;
          await handleUnifiedFileImport(file, true);
        };
        input.click();
      }
    } else {
      await handleImportFromCode(importInputValue.trim());
    }
  };

  // カスタムプロンプトでキャンセルが押された時の処理
  const handleImportPromptCancel = () => {
    setShowImportPrompt(false);
    setImportInputValue('');
  };

  // カスタム確認ダイアログのハンドラー
  const showCustomConfirm = (title: string, callback: () => void) => {
    setConfirmSheetTitle(title);
    setConfirmCallback(() => callback);
    setShowImportConfirm(true);
  };

  // カスタムエクスポート確認ダイアログを表示する関数
  const showCustomExportConfirm = (title: string, callback: (action: 'download' | 'private' | 'public') => void) => {
    setExportSheetTitle(title);
    setExportCallback(() => callback);
    setShowExportConfirm(true);
  };

  const handleConfirmOK = () => {
    setShowImportConfirm(false);
    if (confirmCallback) {
      confirmCallback();
    }
    setConfirmCallback(null);
  };

  const handleConfirmCancel = () => {
    setShowImportConfirm(false);
    setConfirmCallback(null);
  };

  const handleExportAction = (action: 'download' | 'private' | 'public') => {
    if (exportCallback) {
      exportCallback(action);
    }
    setShowExportConfirm(false);
    setExportCallback(null);
  };

  const handleExportCancel = () => {
    setShowExportConfirm(false);
    setExportCallback(null);
  };


  
  // シート全体の統合更新処理（パウチとグループを一度に更新）
  const handleUpdateActiveSheet = useCallback(async (
    pouches: Pouch[] | null,
    groups: Group[] | null,
    isCritical: boolean = false,
    saveToDb: boolean = true,
    sheetParams?: Partial<Sheet>,
    historyActionType?: HistoryActionType,
    forceNewEntry?: boolean,
    sourceSheetId?: string,
    /** キーボード新規キャンセル等: 直近が該当パウチの Add Pouch ならその履歴エントリも削除 */
    stripAddPouchHistoryForPouchId?: string
  ) => {
    if (!activeSheet) {
      isUpdatingActiveSheetRef.current = false;
      return;
    }
    
    // 並行実行を防ぐ
    if (isUpdatingActiveSheetRef.current) {
      return;
    }
    
    // 通常の処理を継続（亡霊検出は削除）

    isUpdatingActiveSheetRef.current = true;
    
    try {
      // 最新のstateをrefから取得
      const currentState = stateRef.current;
      
      // 最新のstateからactiveSheetを取得して比較
      const latestActiveSheet = currentState.sheets.find(s => s.id === activeSheet.id) || activeSheet;
      
      // 最新の状態を取得（最新のactiveSheetを使用）
      let updatedPouches = pouches ? [...pouches] : latestActiveSheet.pouches;
      let updatedGroups = groups ? [...groups] : latestActiveSheet.groups;
      
      
      // 削除中(_isDeleting)のパウチを処理
      const deletingPouches = updatedPouches.filter(p => (p as any)._isDeleting);
      if (deletingPouches.length > 0) {
        
        // 再確認: 削除フラグが確実に設定されていることを確認
        const uiPouches = updatedPouches;
        
        // DB保存用の配列（削除中フラグ付きのパウチを除外）
        const dbPouches = updatedPouches.filter(p => !(p as any)._isDeleting);
        // まずUIを更新して削除アニメーションを開始
        dispatch({ 
          type: 'SET_ACTIVE_SHEET_POUCHES', 
          payload: uiPouches 
        });
        
        // 削除アニメーション後に実際に削除
        setTimeout(() => {
          
          // 400ms後にUIからも削除
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_POUCHES', 
            payload: dbPouches 
          });
        }, 400);
        
        // DB保存用には削除済みの配列を使用（ただし、UI更新は400ms後に実行されるため、ここでは更新しない）
        // updatedPouches = dbPouches; // 即座に削除されるのを防ぐためコメントアウト
      }
      
      // 追加中(_isAdding)のグループを処理
      const addingGroups = updatedGroups.filter(g => (g as any)._isAdding);
      if (addingGroups.length > 0) {
        // アニメーション後にフラグをリセット
        const timerId = setTimeout(() => {
          const groupsWithoutAddingFlag = updatedGroups.map(g => 
            (g as any)._isAdding ? { ...g, _isAdding: false } : g
          );
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutAddingFlag);
          }
        }, 300);
      }
      
      // 削除中(_isDeleting)のグループを処理（ActiveSheetComponentで処理済みの場合はスキップ）
      const deletingGroups = updatedGroups.filter(g => (g as any)._isDeleting);
      if (deletingGroups.length > 0) {
        // 通常の削除処理（Shift+Merge等）の場合は、App.tsxでタイマー処理
        setTimeout(() => {
          const groupsWithoutDeleted = updatedGroups.filter(g => !(g as any)._isDeleting);
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_GROUPS', 
            payload: groupsWithoutDeleted 
          });
        }, 300);
        
        // DB保存用には削除済みの配列を使用
        updatedGroups = updatedGroups.filter(g => !(g as any)._isDeleting);
      }
      
      if (pouches) {
        // 通常の更新（削除中パウチがない場合）
        // パウチの移動フラグをログに出力（デバッグ用）
        const movedPouches = pouches.filter(p => (p as any)._isMoving);
        
        // パウチの値や乗数が変更された場合はキャッシュをクリア
        const hasValueChanges = updatedPouches.some(pouch => {
          const originalPouch = latestActiveSheet.pouches.find(p => p.id === pouch.id);
          return originalPouch && (
            originalPouch.value !== pouch.value ||
            originalPouch.multiplier !== pouch.multiplier ||
            originalPouch.isNegative !== pouch.isNegative
          );
        });
        
        if (hasValueChanges) {
          clearGroupLogicCache();
        }
        
        // 削除中パウチがない場合のみパウチの状態を更新
        if (deletingPouches.length === 0) {
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_POUCHES', 
            payload: updatedPouches 
          });
        }
      }
      
      // グループ更新時は、常にパウチとグループの関係を更新する
      // undo/redo中（html.undoredoクラスが設定されている間）はz-index再計算をスキップ
      const isUndoRedoAnimation = document.documentElement.classList.contains('undoredo');
      if (groups && !isUndoRedoAnimation) {
        
        // GTP作成時はキャッシュをクリアしない（パウチ位置が元に戻るのを防ぐ）
        const isGTPCreation = pouches && pouches.length > latestActiveSheet.pouches.length && 
          pouches.some(p => p.isGroupTotalPouch && !latestActiveSheet.pouches.some(ap => ap.id === p.id));
        
        if (!isGTPCreation) {
          // グループが変更された場合はキャッシュをクリア
          clearGroupLogicCache();
          
          // グループリサイズ時は特に境界計算のキャッシュをクリア
          clearGroupResizeCache();
        }
        
        // Z-indexを再計算
        const { getPathBounds } = await import('./logic/GroupLogic');
        
        // 全グループのboundsを事前計算
        const groupBoundsMap = new Map();
        updatedGroups.forEach(group => {
          groupBoundsMap.set(group.id, getPathBounds(group.path));
        });

        // ミュートグループの事前抽出とbounds計算
        const muteGroups = updatedGroups.filter(g => g.color === 'mute');
        const muteBoundsMap = new Map();
        muteGroups.forEach(group => {
          muteBoundsMap.set(group.id, getPathBounds(group.path));
        });


        // 各グループについて、含まれるパウチとミュートグループとの関係を再計算
        updatedGroups = updatedGroups.map(group => {
          const oldPouchIds = group.pouches || [];
          const newPouchIds: string[] = [];
          const bounds = groupBoundsMap.get(group.id);
          
          if (bounds) {
            // 各パウチについて、グループ内に含まれるかを確認
            for (const pouch of updatedPouches) {
              const isInBounds = 
                pouch.x >= bounds.left && 
                pouch.x <= bounds.right && 
                pouch.y >= bounds.top && 
                pouch.y <= bounds.bottom;
              
              if (isInBounds) {
                newPouchIds.push(pouch.id);
              }
            }
          }
          
          // ここでログを出力する代わりに、デバウンスされた関数を呼び出す
          debouncedLog();
          
          // このグループがミュートグループに完全に内包されているかを確認
          let isInMuteGroup = false;
          
          // ミュートグループが存在する場合のみチェック
          if (bounds && muteGroups.length > 0) {
            for (const muteGroup of muteGroups) {
              const muteBounds = muteBoundsMap.get(muteGroup.id);
              if (muteBounds) {
                // グループがミュートグループに完全に内包されているかを確認
                if (
                  bounds.left >= muteBounds.left &&
                  bounds.right <= muteBounds.right &&
                  bounds.top >= muteBounds.top &&
                  bounds.bottom <= muteBounds.bottom
                ) {
                  isInMuteGroup = true;
                  break;
                }
              }
            }
          }
          
          // 変更があるかをチェック
          const hasChanged = 
            newPouchIds.length !== oldPouchIds.length || 
            newPouchIds.some(id => !oldPouchIds.includes(id)) ||
            oldPouchIds.some(id => !newPouchIds.includes(id));
          
          // 変更がある場合のみ新しいオブジェクトを返し、そうでなければ元のオブジェクトを返す
          return hasChanged ? {
            ...group,
            pouches: newPouchIds,
            _animateSubtotal: hasChanged,
            _isInMuteGroup: isInMuteGroup
          } : {
            ...group,
            _isInMuteGroup: isInMuteGroup
          };
        });
          
        // グループの状態を更新
        dispatch({ 
          type: 'SET_ACTIVE_SHEET_GROUPS', 
          payload: updatedGroups 
        });
      }

      // --- 帳尻パウチ（Balancing Pouch）再計算 ---
      let balancingPouchId = latestActiveSheet.balancingPouchId ?? null;
      let balancingTargetTotal = latestActiveSheet.balancingTargetTotal;

      if (balancingPouchId) {
        const bpPouch = updatedPouches.find(p => p.id === balancingPouchId);

        if (!bpPouch || bpPouch._isDeleting) {
          // BP パウチが削除された → BP をクリア
          balancingPouchId = null;
          balancingTargetTotal = undefined;
        } else if (historyActionType === HistoryActionType.MERGE_POUCH) {
          // マージ発生 → BP をオフにする
          balancingPouchId = null;
          balancingTargetTotal = undefined;
        } else if (balancingTargetTotal !== undefined) {
          // 他パウチの変更 → BP の value を自動調整
          updatedPouches = applyBalancingPouch(updatedPouches, updatedGroups, balancingPouchId, balancingTargetTotal);

          // UI への即時反映
          if (deletingPouches.length === 0) {
            dispatch({ type: 'SET_ACTIVE_SHEET_POUCHES', payload: updatedPouches });
          }
        }
      }

      // MOVE_POUCH でBP状態を移動先シートに引き継ぐ
      if (historyActionType === HistoryActionType.MOVE_POUCH && sourceSheetId) {
        const srcSheet = stateRef.current.sheets.find(s => s.id === sourceSheetId);
        if (srcSheet?.balancingPouchId) {
          const newPouch = updatedPouches.find(p =>
            !latestActiveSheet.pouches.some(op => op.id === p.id)
          );
          if (newPouch && newPouch.id === srcSheet.balancingPouchId) {
            balancingPouchId = newPouch.id;
            balancingTargetTotal = calculateSheetTotal(updatedPouches, updatedGroups);
          }
        }
      }

      // 重要な更新があるかチェック（パウチの場合）
      let hasCriticalUpdates = isCritical;
      const criticalUpdateReason: string[] = [];
      
      if (isCritical) {
        criticalUpdateReason.push('明示的なisCriticalフラグ');
      }
      
      if (pouches) {
        const hasPouchStateFlags = pouches.some(p => 
          (p as any)._isDragging || 
          // (p as any)._animateMultiplier ||
          // (p as any)._animateSignChange ||
          (p as any)._isMoving
        );
        
        if (hasPouchStateFlags) {
          hasCriticalUpdates = true;
          
          if (pouches.some(p => (p as any)._isDragging)) criticalUpdateReason.push('_isDragging');
          // if (pouches.some(p => (p as any)._animateMultiplier)) criticalUpdateReason.push('_animateMultiplier');
          // if (pouches.some(p => (p as any)._animateSignChange)) criticalUpdateReason.push('_animateSignChange');
          if (pouches.some(p => (p as any)._isMoving)) criticalUpdateReason.push('_isMoving');
        }
      }
      
      // DBに保存しない場合は、pouches/groupsのみ更新して終了
      if (!saveToDb) {
        // stateRef.currentから最新シートを取得（クロージャのstateは古い可能性がある）
        const currentSheet = stateRef.current.sheets.find(s => s.id === activeSheet.id);
        if (!currentSheet) return;
        
        const updatedSheet = {
          ...currentSheet,
          pouches: updatedPouches,
          groups: updatedGroups
        };
        
        // 既存のsetTimeoutをクリア（古いデータで上書きされるのを防ぐ）
        if (pouchUpdateTimeoutRef.current) {
          clearTimeout(pouchUpdateTimeoutRef.current);
          pouchUpdateTimeoutRef.current = null;
        }
        
        dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
        
        // stateRefも同期的に更新（useEffectのstateRef同期前に別の操作が走った場合の汚染を防ぐ）
        stateRef.current = {
          ...stateRef.current,
          sheets: stateRef.current.sheets.map(s =>
            s.id === updatedSheet.id ? updatedSheet : s
          )
        };
        
        isUpdatingActiveSheetRef.current = false;
        return;
      }
      
      // シートメタデータの更新検出（グループカラー、パウチカラー、パウチ値）
      let newLastGroupColor = latestActiveSheet.lastGroupColor;
      let newLastPouchColor = latestActiveSheet.lastPouchColor;
      let newLastPouchValue = latestActiveSheet.lastPouchValue;
      
      // 1. グループカラー変更の検出
      if (groups) {
        // 更新があったグループのうち、色が変更されたものを探す
        const colorChangedGroup = updatedGroups.find(group => {
          // 元のグループを見つける
          const originalGroup = latestActiveSheet.groups.find(g => g.id === group.id);
          // 色が変更されたかチェック
          return originalGroup && originalGroup.color !== group.color;
        });
        
        // 色が変更されたグループがあれば、そのカラーをlastGroupColorとして使用
        if (colorChangedGroup) {
          
          newLastGroupColor = colorChangedGroup.color;
        }
      }
      
      // 2. パウチカラー変更の検出
      if (pouches) {
        // カラー変更されたパウチを探す
        const colorChangedPouch = updatedPouches.find(pouch => {
          // 元のパウチを見つける
          const originalPouch = latestActiveSheet.pouches.find(p => p.id === pouch.id);
          // 色が変更されたかチェック
          return originalPouch && originalPouch.color !== pouch.color;
        });
        
        // 色が変更されたパウチがあれば、そのカラーをlastPouchColorとして使用（GTPは除外）
        if (colorChangedPouch && !colorChangedPouch.isGroupTotalPouch) {
          
          newLastPouchColor = colorChangedPouch.color;
        }
        
        // 3. 新規作成パウチがあれば、その値とカラーを保存（GTPは除外、GTPの自動更新時も除外）
        const newPouch = updatedPouches.find(pouch => 
          !latestActiveSheet.pouches.some(op => op.id === pouch.id) && !pouch.isGroupTotalPouch
        );
        
        if (newPouch && historyActionType) {
          
          newLastPouchColor = newPouch.color;
          newLastPouchValue = newPouch.value;
        }
      }
      
      // colorLegendsの初期化チェック（ActiveSheetComponentからの移行）
      // 注意: stateRef.currentの更新はuseEffectで非同期に行われるため、
      // handleUpdateColorLegendで更新した直後にパウチ操作をすると古いデータを参照してしまう問題がある
      // そのため、latestActiveSheetではなくactiveSheet（useCallbackの依存配列で最新が保証される）を優先使用
      let initialColorLegends = activeSheet.colorLegends || latestActiveSheet.colorLegends;
      
      // sheetParamsパラメータがある場合は、それを適用する
      if (sheetParams && sheetParams.colorLegends) {
        initialColorLegends = sheetParams.colorLegends;
      }
      // sheetParamsがない場合で、colorLegendsが空の場合は、POUCH_COLORSに基づいて初期化
      else if (!initialColorLegends || initialColorLegends.length === 0) {
        // colorLegendsが空の場合は、POUCH_COLORSに基づいて初期化
        initialColorLegends = POUCH_COLORS.map(color => ({
          colorId: color.id,
          legend: ''
        }));
      }
      
      // sheetParamsパラメータがある場合は、その他のパラメータも適用する
      if (sheetParams) {
        if (sheetParams.lastGroupColor) {
          newLastGroupColor = sheetParams.lastGroupColor;
        }
        if (sheetParams.lastPouchColor) {
          newLastPouchColor = sheetParams.lastPouchColor;
        }
        if (sheetParams.lastPouchValue) {
          newLastPouchValue = sheetParams.lastPouchValue;
        }
      }
      
      // historyActionTypeがある場合は履歴エントリを追加
      // 最新のactiveSheetを使用（古い参照を避ける）
      const currentActiveSheet = latestActiveSheet;
      let updatedSheetWithHistory = { ...currentActiveSheet };
      if (historyActionType) {
        // 履歴作成処理開始 - in-actionクラスを追加
        isInHistoryActionRef.current = true;
        document.documentElement.classList.add('in-action');
        
        // 前回のタイマーをクリア
        if (historyActionTimerRef.current !== null) {
          clearTimeout(historyActionTimerRef.current);
          historyActionTimerRef.current = null;
        }
        
        const { HistoryLogic } = await import('./utils/HistoryLogic');
        
        // 説明文を生成
        let description: string = historyActionType;
        
        // シート間移動・複製の場合は移動元シート名を含む説明文を生成
        
        if (sourceSheetId && (
          historyActionType === HistoryActionType.MOVE_POUCH ||
          historyActionType === HistoryActionType.MOVE_GROUP ||
          historyActionType === HistoryActionType.CLONE_POUCH ||
          historyActionType === HistoryActionType.CLONE_GROUP
        )) {
          const sourceSheet = stateRef.current.sheets.find(s => s.id === sourceSheetId);
          const sourceSheetName = sourceSheet?.title || 'Unknown Sheet';
          
          if (historyActionType === HistoryActionType.MOVE_POUCH) {
            description = `Move Pouch from ${sourceSheetName}`;
          } else if (historyActionType === HistoryActionType.MOVE_GROUP) {
            description = `Move Group from ${sourceSheetName}`;
          } else if (historyActionType === HistoryActionType.CLONE_POUCH) {
            description = `Clone Pouch from ${sourceSheetName}`;
          } else if (historyActionType === HistoryActionType.CLONE_GROUP) {
            description = `Clone Group from ${sourceSheetName}`;
          }
        }
        
        // アクションタイプに応じたtargetIdを特定
        let targetId: string | undefined;
        
        switch (historyActionType) {
          case HistoryActionType.ADD_POUCH:
          case HistoryActionType.EDIT_POUCH_VALUE:
          case HistoryActionType.MOVE_POUCH:
          case HistoryActionType.CHANGE_POUCH_COLOR:
          case HistoryActionType.CLONE_POUCH:
          case HistoryActionType.EDIT_POUCH_TITLE:
          case HistoryActionType.PASTE_BULK_UPDATE:
            // パウチに関連するアクション - 変更されたパウチを特定
            if (pouches) {
              // 値、位置、色、タイトルが変更されたパウチを特定
              const modifiedPouch = updatedPouches.find(pouch => {
                const originalPouch = latestActiveSheet.pouches.find(p => p.id === pouch.id);
                if (!originalPouch) return historyActionType === HistoryActionType.ADD_POUCH; // ADD_POUCHの場合は新規作成パウチを対象とする
                
                // 操作タイプに応じて変更を検出
                if (historyActionType === HistoryActionType.EDIT_POUCH_VALUE || 
                    historyActionType === HistoryActionType.PASTE_BULK_UPDATE) {
                  return originalPouch.value !== pouch.value || 
                         originalPouch.multiplier !== pouch.multiplier;
                }
                else if (historyActionType === HistoryActionType.MOVE_POUCH) {
                  return originalPouch.x !== pouch.x || originalPouch.y !== pouch.y;
                }
                else if (historyActionType === HistoryActionType.CHANGE_POUCH_COLOR) {
                  return originalPouch.color !== pouch.color;
                }
                else if (historyActionType === HistoryActionType.EDIT_POUCH_TITLE) {
                  return originalPouch.title !== pouch.title;
                }
                // CLONE_POUCHの場合は新規作成されたパウチ
                return false;
              });
              
              // 操作されたパウチのIDをtargetIdに設定
              if (modifiedPouch) {
                targetId = modifiedPouch.id;
              }
              // ADD_POUCHまたはCLONE_POUCHの場合は新規作成されたパウチを特定
              else if (historyActionType === HistoryActionType.ADD_POUCH || 
                       historyActionType === HistoryActionType.CLONE_POUCH) {
                const newPouch = updatedPouches.find(p => 
                  !latestActiveSheet.pouches.some(op => op.id === p.id)
                );
                if (newPouch) targetId = newPouch.id;
              }
            }
            break;

          case HistoryActionType.ADD_GROUP:
          case HistoryActionType.MOVE_GROUP:
          case HistoryActionType.RESIZE_GROUP:
          case HistoryActionType.CHANGE_GROUP_COLOR:
          case HistoryActionType.CLONE_GROUP:
          case HistoryActionType.EDIT_GROUP_TITLE:
            // グループに関連するアクション - 変更されたグループを特定
            if (groups) {
              // 追加、移動、リサイズ、色変更、タイトル変更されたグループを特定
              const modifiedGroup = updatedGroups.find(group => {
                const originalGroup = latestActiveSheet.groups.find(g => g.id === group.id);
                if (!originalGroup) return false; // 新規作成のグループ
                
                // 操作タイプに応じて変更を検出
                if (historyActionType === HistoryActionType.MOVE_GROUP) {
                  // パスが変更されているか確認（移動）
                  return originalGroup.path !== group.path;
                }
                else if (historyActionType === HistoryActionType.RESIZE_GROUP) {
                  // パスが変更されているか確認（リサイズ）
                  return originalGroup.path !== group.path;
                }
                else if (historyActionType === HistoryActionType.CHANGE_GROUP_COLOR) {
                  // 色が変更されているか確認
                  return originalGroup.color !== group.color;
                }
                else if (historyActionType === HistoryActionType.EDIT_GROUP_TITLE) {
                  // タイトルが変更されているか確認
                  return originalGroup.title !== group.title;
                }
                // その他のケース
                return false;
              });
              
              // 操作されたグループのIDをtargetIdに設定
              if (modifiedGroup) {
                targetId = modifiedGroup.id;
              }
              // ADD_GROUPまたはCLONE_GROUPの場合は新規作成されたグループを特定
              else if (historyActionType === HistoryActionType.ADD_GROUP || 
                      historyActionType === HistoryActionType.CLONE_GROUP) {
                const newGroup = updatedGroups.find(g => 
                  !latestActiveSheet.groups.some(og => og.id === g.id)
                );
                if (newGroup) targetId = newGroup.id;
              }
            }
            break;
            
          // その他のアクションタイプはtargetIdを使用しない
          default:
            targetId = undefined;
        }
        
        // 履歴エントリを追加
        updatedSheetWithHistory = HistoryLogic.addEntry(
          currentActiveSheet,
          updatedGroups,
          updatedPouches,
          historyActionType,
          description,
          targetId,
          forceNewEntry
        );
        
        // 履歴作成処理完了後にタイマーでin-actionクラスを削除
        historyActionTimerRef.current = window.setTimeout(() => {
          if (isInHistoryActionRef.current) {
            isInHistoryActionRef.current = false;
            document.documentElement.classList.remove('in-action');
            historyActionTimerRef.current = null;
          }
        }, 600); // 600ms後に削除
      } else if (stripAddPouchHistoryForPouchId) {
        const { HistoryLogic } = await import('./utils/HistoryLogic');
        const stripped = HistoryLogic.stripLatestHistoryEntryIfAddPouchForTarget(
          currentActiveSheet,
          stripAddPouchHistoryForPouchId
        );
        if (stripped) {
          updatedSheetWithHistory = {
            ...updatedSheetWithHistory,
            history: stripped.history,
            historyIndex: stripped.historyIndex
          };
        }
      }
      
      // BP状態をstateRefから再読み取り（await中にhandleToggleBalancingPouchで変更された可能性があるため）
      const freshSheet = stateRef.current.sheets.find(s => s.id === activeSheet?.id);
      const freshBalancingPouchId = freshSheet ? freshSheet.balancingPouchId : balancingPouchId;
      const freshBalancingTargetTotal = freshSheet ? freshSheet.balancingTargetTotal : balancingTargetTotal;

      // DBに保存するための現在の完全なシート情報
      const updatedSheet = {
        ...updatedSheetWithHistory,
        pouches: updatedPouches,
        groups: updatedGroups,
        lastGroupColor: newLastGroupColor,
        lastPouchColor: newLastPouchColor,
        lastPouchValue: (() => {
          if (historyActionType === HistoryActionType.EDIT_POUCH_VALUE || 
              historyActionType === HistoryActionType.PASTE_BULK_UPDATE) {
            const changedPouch = updatedPouches.find(p => {
              const originalPouch = currentActiveSheet.pouches.find(op => op.id === p.id);
              return originalPouch && originalPouch.value !== p.value && !p.isGroupTotalPouch;
            });
            if (changedPouch) {
              if (changedPouch.id === currentActiveSheet.balancingPouchId) {
                return currentActiveSheet.lastPouchValue;
              }
              return changedPouch.value;
            }
          }
          return currentActiveSheet.lastPouchValue;
        })(),
        colorLegends: initialColorLegends,
        snapshots: updatedSheetWithHistory.snapshots || currentActiveSheet.snapshots || [],
        lastModified: new Date(),
        balancingPouchId: freshBalancingPouchId,
        balancingTargetTotal: freshBalancingTargetTotal
      };
      
        // 前回のタイマーをクリア
        if (pouchUpdateTimeoutRef.current !== null) {
          clearTimeout(pouchUpdateTimeoutRef.current);
          pouchUpdateTimeoutRef.current = null;
        }
      
      if (hasCriticalUpdates) {
        // 重要な更新は即時実行
        // 即時実行時も最新のstateから colorLegends と title を取得（古いクロージャで上書きされる問題を防ぐ）
        const latestStateForCritical = stateRef.current;
        const latestSheetForCritical = latestStateForCritical.sheets.find(s => s.id === activeSheet.id);
        
        const finalUpdatedSheetCritical = {
          ...updatedSheet,
          title: latestSheetForCritical?.title || updatedSheet.title,
          colorLegends: latestSheetForCritical?.colorLegends || updatedSheet.colorLegends,
          // BP状態も最新のものを使用（handleToggleBalancingPouchとの競合防止）
          balancingPouchId: latestSheetForCritical ? latestSheetForCritical.balancingPouchId : updatedSheet.balancingPouchId,
          balancingTargetTotal: latestSheetForCritical ? latestSheetForCritical.balancingTargetTotal : updatedSheet.balancingTargetTotal,
        };
        
        dispatch({ type: 'UPDATE_SHEET', payload: finalUpdatedSheetCritical });
        await SheetStorage.saveSheet(finalUpdatedSheetCritical);
        isUpdatingActiveSheetRef.current = false;
        return;
      }
      
      // 通常の更新はデバウンスして実行（500ms待機）
      pouchUpdateTimeoutRef.current = window.setTimeout(async () => {
        // タイマー実行時に最新のstateから最新のシート情報を取得
        const latestState = stateRef.current;
        const latestSheet = latestState.sheets.find(s => s.id === activeSheet.id);
        
        // 最新のシート情報と、保存予定だったupdatedSheetをマージ
        const finalUpdatedSheet = {
          ...updatedSheet,
          // 最新のtitleやその他のプロパティを反映
          title: latestSheet?.title || updatedSheet.title,
          // colorLegendsも最新のものを使用（凡例タイトルが消える問題の修正）
          colorLegends: latestSheet?.colorLegends || updatedSheet.colorLegends,
          // BP状態も最新のものを使用（handleToggleBalancingPouchとの競合防止）
          balancingPouchId: latestSheet ? latestSheet.balancingPouchId : updatedSheet.balancingPouchId,
          balancingTargetTotal: latestSheet ? latestSheet.balancingTargetTotal : updatedSheet.balancingTargetTotal,
        };
        
        dispatch({ type: 'UPDATE_SHEET', payload: finalUpdatedSheet });
        await SheetStorage.saveSheet(finalUpdatedSheet);
        pouchUpdateTimeoutRef.current = null;
      }, 500);
    } catch (error) {
      console.error('シート情報の保存に失敗しました:', error);
    } finally {
      // 並行実行フラグをリセット
      isUpdatingActiveSheetRef.current = false;
    }
  }, [activeSheet, debouncedLog]);
  
  // 帳尻パウチ（BP）トグル処理
  const handleToggleBalancingPouch = useCallback(async (pouchId: string) => {
    if (!activeSheet) return;
    const currentSheet = stateRef.current.sheets.find(s => s.id === activeSheet.id) || activeSheet;

    let newBalancingPouchId: string | null;
    let newBalancingTargetTotal: number | undefined;

    if (currentSheet.balancingPouchId === pouchId) {
      newBalancingPouchId = null;
      newBalancingTargetTotal = undefined;
    } else {
      newBalancingPouchId = pouchId;
      newBalancingTargetTotal = currentSheet.balancingTargetTotal ?? calculateSheetTotal(currentSheet.pouches, currentSheet.groups);
    }

    // BP 状態を更新したシートを作成
    const sheetWithNewBp: Sheet = {
      ...currentSheet,
      balancingPouchId: newBalancingPouchId,
      balancingTargetTotal: newBalancingTargetTotal
    };

    // 履歴エントリを作成（新しい BP 状態を含むシートで addEntry）
    const { HistoryLogic } = await import('./utils/HistoryLogic');
    let bpDescription: string;
    if (!newBalancingPouchId) {
      bpDescription = 'Disable Balancing Pouch';
    } else if (currentSheet.balancingPouchId && currentSheet.balancingPouchId !== pouchId) {
      bpDescription = 'Change Balancing Pouch';
    } else {
      bpDescription = 'Enable Balancing Pouch';
    }
    const updatedSheet = HistoryLogic.addEntry(
      sheetWithNewBp,
      currentSheet.groups,
      currentSheet.pouches,
      HistoryActionType.TOGGLE_BALANCING_POUCH,
      bpDescription,
      pouchId
    );

    // 既存のデバウンスタイマーをキャンセル（古いBP状態での上書き防止）
    if (pouchUpdateTimeoutRef.current !== null) {
      clearTimeout(pouchUpdateTimeoutRef.current);
      pouchUpdateTimeoutRef.current = null;
    }

    dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });

    // stateRefも同期的に更新（デバウンス保存との競合防止）
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s =>
        s.id === updatedSheet.id ? updatedSheet : s
      )
    };

    await SheetStorage.saveSheet(updatedSheet);
  }, [activeSheet]);

  // 統計情報更新処理
  const handleUpdateStatistics = useCallback((statistics: Statistics) => {
    setStats(statistics);
  }, []);
  
  // デバイスタイプ更新処理
  const handleUpdateDeviceType = useCallback((isTouchDevice: boolean) => {
    // appStateのisTouchDeviceを更新
    if (state.isTouchDevice !== isTouchDevice) {
      // state内のisTouchDeviceを直接変更するような操作はないためカスタムロジックで実装
      const newState = {
        ...state,
        isTouchDevice: isTouchDevice
      };
      
      // reducerを使わずに状態を更新
      (state as any).isTouchDevice = isTouchDevice;
      
      // デバッグ出力
      
      // HTML要素にタッチデバイス用のクラスを更新
      if (isTouchDevice) {
        document.documentElement.classList.add('touch-device');
      } else {
        document.documentElement.classList.remove('touch-device');
      }
    }
  }, [state]);

  // サイドバーが閉じたときの遅延削除処理
  useEffect(() => {
    if (!state.isRightSidebarOpen && !state.isRightSidebarFullyClosed) {
      // サイドバーが閉じた場合、300ms後にコンポーネントを削除
      const timer = setTimeout(() => {
        dispatch({ type: 'SET_RIGHT_SIDEBAR_FULLY_CLOSED', payload: true });
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [state.isRightSidebarOpen, state.isRightSidebarFullyClosed]);
  
  // 凡例更新処理
  const handleUpdateColorLegend = useCallback((sheetId: string, colorId: string, legend: string) => {
    // stateRef.currentから最新シートを取得（クロージャのactiveSheetは古い可能性がある）
    const latestSheet = stateRef.current.sheets.find(s => s.id === sheetId);
    if (!latestSheet) return;

    const updatedColorLegends = latestSheet.colorLegends?.map(cl => 
      cl.colorId === colorId ? { ...cl, legend } : cl
    ) || [];
    
    const updatedSheet = { ...latestSheet, colorLegends: updatedColorLegends };
    dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
    SheetStorage.saveSheet(updatedSheet);
    
    // stateRefも同期的に更新（setTimeout内のクロージャが古いデータを参照する問題を防ぐ）
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s => 
        s.id === sheetId ? updatedSheet : s
      )
    };
  }, []);
  
  // 色別合計計算処理
  const handleCalculateColorTotal = useCallback((colorId: string) => {
    if (activeSheet) {
      return calculateColorTotal(activeSheet.pouches, colorId, activeSheet.groups);
    }
    return 0;
  }, [activeSheet]);
  
  // 色別カウント計算処理
  const handleCalculateColorCount = useCallback((colorId: string) => {
    if (activeSheet) {
      return calculateColorCount(activeSheet.pouches, colorId, activeSheet.groups);
    }
    return 0;
  }, [activeSheet]);
  
  // 編集確定処理
  const handleEditConfirm = useCallback(() => {
    // 編集確定時の処理（必要に応じて実装）
  }, []);
  
  // シート切替時にウィンドウタイトルを「POUCHES」に戻す
  useEffect(() => {
    document.title = 'POUCHES';
  }, [state.activeSheetId]);

  // シート切替時の処理
  useEffect(() => {
    if (state.activeSheetId) {
      // 前のシートのキャッシュを削除
      const previousSheet = state.sheets.find(s => s.id !== state.activeSheetId);
      if (previousSheet) {
      }
      
      // 前回のタイマーをクリア
      if (switchTimerRef.current !== null) {
        clearTimeout(switchTimerRef.current);
      }
      
      // グループ更新タイマーもクリア
      if (groupUpdateTimeoutRef.current !== null) {
        clearTimeout(groupUpdateTimeoutRef.current);
        groupUpdateTimeoutRef.current = null;
        pendingGroupUpdateRef.current = null;
      }
      
      // パウチ更新タイマーもクリア
      if (pouchUpdateTimeoutRef.current !== null) {
        clearTimeout(pouchUpdateTimeoutRef.current);
        pouchUpdateTimeoutRef.current = null;
        pendingPouchUpdateRef.current = null;
      }

      isSheetSwitchingRef.current = true;
      setIsSheetSwitching(true);
      
      // シート切り替え時に全パウチをリセット
      if (activeSheetRef.current) {
        // ActiveSheetComponentのpouchesRefから現在のパウチを取得してリセット
        const currentPouches = (activeSheetRef.current as any).pouchesRef?.current || [];
        const resetPouches = currentPouches.map((pouch: any) => ({
          ...pouch,
        }));
        activeSheetRef.current.setPouches(resetPouches);
      }
      
      switchTimerRef.current = window.setTimeout(() => {
        isSheetSwitchingRef.current = false;
        setIsSheetSwitching(false);
        switchTimerRef.current = null;
      }, 600);

      return () => {
        if (switchTimerRef.current !== null) {
          clearTimeout(switchTimerRef.current);
        }
      };
    }
  }, [state.activeSheetId]);

  // 履歴コンポーネントからの状態更新ハンドラ
  const handleHistoryStateUpdate = async (newSheet: Sheet, newPouches: Pouch[], newGroups: Group[]) => {
    // 履歴からの更新であることを示すフラグ
    const isHistoryUpdate = true;
    // スナップショット関連の変更を検出
    const isSnapshotChange = 
      newSheet.snapshots?.length !== activeSheet?.snapshots?.length ||
      JSON.stringify(newSheet.snapshots?.map(s => s.id).sort()) !== 
      JSON.stringify(activeSheet?.snapshots?.map(s => s.id).sort());
    
    // スナップショット復元の判定
    const isSnapshotRestore = newSheet.history?.[newSheet.historyIndex]?.type === HistoryActionType.RESTORE_SNAPSHOT;
    
    // 削除対象のパウチとグループを見つける
    const deletingPouches = newPouches.filter(p => (p as any)._isDeleting);
    const deletingGroups = newGroups.filter(g => (g as any)._isDeleting);
    
    if (deletingPouches.length > 0 || deletingGroups.length > 0) {
      // 削除アニメーションを表示するため、一時的に削除フラグ付きのパウチとグループを含めて更新
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_POUCHES', 
        payload: newPouches 
      });
      
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_GROUPS', 
        payload: newGroups 
      });
      
      // アクティブシートを更新
      dispatch({ 
        type: 'UPDATE_SHEET', 
        payload: { ...newSheet, pouches: newPouches, groups: newGroups }
      });
      
      // 常に永続化する - historyIndexの変更を保存するため
        await SheetStorage.saveSheet({ ...newSheet, pouches: newPouches, groups: newGroups });
      
      // 削除アニメーション後に実際に削除されたパウチとグループを除外
      setTimeout(async () => {
        // 履歴からのredoの場合は、元々の履歴状態（削除フラグなし）を使用
        // 通常の削除の場合は、削除フラグ付きパウチを除外
        const pouchesWithoutDeleted = (isHistoryUpdate || isSnapshotRestore)
          ? newPouches.filter(p => !(p as any)._isDeleting)  // 履歴更新でも削除フラグ付きパウチを除外
          : newPouches.filter(p => !(p as any)._isDeleting);  // 通常の削除の場合はフィルタリング
        
        // グループの削除処理も実行
        const groupsWithoutDeleted = newGroups.filter(g => !(g as any)._isDeleting);
        
        // アクティブシートを更新
        const updatedSheet = { ...newSheet, pouches: pouchesWithoutDeleted, groups: groupsWithoutDeleted };
        dispatch({ 
          type: 'UPDATE_SHEET', 
          payload: updatedSheet
        });
        
        // ActiveSheetComponentの状態を更新
        if (activeSheetRef.current) {
          activeSheetRef.current.setPouches(pouchesWithoutDeleted);
          activeSheetRef.current.setGroups(groupsWithoutDeleted);
          
          // スナップショット復元時に強制的に再レンダリング
          if (isSnapshotRestore) {
            activeSheetRef.current.forceUpdate();
          }
        }
        
        // 永続化（アニメーション完了後の最終状態）
          await SheetStorage.saveSheet(updatedSheet);
      }, 400); // 削除アニメーションの持続時間に合わせる
      
      // 履歴からの更新でもaddingフラグの削除処理を実行
      const addingGroups = newGroups.filter(g => (g as any)._isAdding);
      if (addingGroups.length > 0) {
        // アニメーション後にフラグをリセット
        const timerId = setTimeout(() => {
          const groupsWithoutAddingFlag = newGroups.map(g => 
            (g as any)._isAdding ? { ...g, _isAdding: false } : g
          );
          // ヒストリーエントリを作成せずに状態を更新
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutAddingFlag);
          }
        }, 300);
      }
    } else {
      // 通常の更新（削除するパウチがない場合）
      
      // 追加中(_isAdding)のグループを処理
      const addingGroups = newGroups.filter(g => (g as any)._isAdding);
      if (addingGroups.length > 0) {
        // アニメーション後にフラグをリセット
        const timerId = setTimeout(() => {
          const groupsWithoutAddingFlag = newGroups.map(g => 
            (g as any)._isAdding ? { ...g, _isAdding: false } : g
          );
          // ヒストリーエントリを作成せずに状態を更新
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutAddingFlag);
          }
        }, 300);
      }
      
      // 削除中(_isDeleting)のグループを処理
      const deletingGroups = newGroups.filter(g => (g as any)._isDeleting);
      if (deletingGroups.length > 0) {
        // アニメーション後にグループを実際に削除
        setTimeout(() => {
          const groupsWithoutDeleted = newGroups.filter(g => !(g as any)._isDeleting);
          dispatch({ 
            type: 'SET_ACTIVE_SHEET_GROUPS', 
            payload: groupsWithoutDeleted 
          });
          
          // ActiveSheetComponentの状態も更新
          if (activeSheetRef.current) {
            activeSheetRef.current.setGroups(groupsWithoutDeleted);
          }
        }, 300);
      }
      
      // アクティブシートを更新
      dispatch({ type: 'UPDATE_SHEET', payload: newSheet });
      
      // stateRefも同期的に更新（履歴操作後のパウチ削除等で古いデータを参照する問題を防ぐ）
      stateRef.current = {
        ...stateRef.current,
        sheets: stateRef.current.sheets.map(s => 
          s.id === newSheet.id ? newSheet : s
        )
      };
      
      // パウチとグループの状態をセット
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_POUCHES', 
        payload: newPouches 
      });
      
      dispatch({ 
        type: 'SET_ACTIVE_SHEET_GROUPS', 
        payload: newGroups 
      });
      
      // ActiveSheetComponentの状態を更新
      if (activeSheetRef.current) {
        activeSheetRef.current.setPouches(newPouches);
        activeSheetRef.current.setGroups(newGroups);
        
        // スナップショット復元時に強制的に再レンダリング
        if (isSnapshotRestore) {
          activeSheetRef.current.forceUpdate();
        }
      }
      
      // 常に永続化する - historyIndexの変更を保存するため
        await SheetStorage.saveSheet(newSheet);
    }
  };

  // ウィンドウ間通信でファイルインポートを処理
  const handleFileImportMessage = useCallback(async (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    
    if (event.data.type === 'IMPORT_POUCHES_FILE' && event.data.fileData) {
      try {
        
        // Base64データからファイルを復元
        const response = await fetch(event.data.fileData);
        const blob = await response.blob();
        const fileName = event.data.fileName || 'imported.pouches';
        const originalLastModified = event.data.lastModified || Date.now();
        const file = new File([blob], fileName, { 
          type: 'application/pouches',
          lastModified: originalLastModified
        });
        
        
        await importSheetFromFile(file);
        
        // ウィンドウにフォーカスを当てる
        window.focus();
      } catch (error) {
        console.error('ウィンドウ間通信でのファイルインポートに失敗しました:', error);
      }
    }
  }, [importSheetFromFile]);

  // ウィンドウ間通信のリスナーを設定
  useEffect(() => {
    window.addEventListener('message', handleFileImportMessage);
    return () => {
      window.removeEventListener('message', handleFileImportMessage);
    };
  }, [handleFileImportMessage]);

  // Electron: .pouches ファイルの open-file イベントを処理
  useEffect(() => {
    const electron = (window as unknown as { electron?: { onOpenFile: (cb: (p: string) => void) => () => void; readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }> } }).electron;
    if (!electron?.onOpenFile) return;
    const cleanup = electron.onOpenFile(async (filePath: string) => {
      if (!filePath.endsWith('.pouches')) return;
      try {
        const result = await electron.readFile(filePath);
        if (!result.success || !result.content) {
          console.error('Electron: Failed to read file');
          return;
        }
        const file = new File([result.content], result.fileName || 'imported.pouches', {
          type: 'application/pouches'
        });
        await importSheetFromFile(file, false, false, filePath);
      } catch (error) {
        console.error('Electron: Import failed', error);
      }
    });
    return cleanup;
  }, [importSheetFromFile]);

  // キーボードショートカットのリスナーを設定
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt+N (Mac/Windows共通) で新規シート作成
      const isAltKey = e.altKey;
      const isNKey = e.key === 'n' || e.key === 'N' || e.code === 'KeyN';
      
      if (isAltKey && isNKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleCreateNewSheet();
        return false;
      }
      
      // F3 で左サイドバーの開閉トグル
      if (e.key === 'F3') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleMenu();
        return false;
      }
      
      // F4 で右サイドバーの開閉トグル
      if (e.key === 'F4') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleSidebar();
        return false;
      }
      
      // Esc でドラッグを強制キャンセル
      if (e.key === 'Escape') {
        const tag = document.activeElement?.tagName;
        // テキスト入力中は編集側の Escape を優先（グループタイトルは textarea）
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          return false;
        }
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // ドラッグ状態を強制リセット
        const dragCancelEvent = new CustomEvent('forceDragCancel');
        document.dispatchEvent(dragCancelEvent);
        return false;
      }
    };

    // イベントリスナーを設定
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleCreateNewSheet, toggleMenu, toggleSidebar]);

  // 強制アンドゥイベントリスナー
  useEffect(() => {
    const handleForceUndo = () => {
      if (activeSheet) {
        // 履歴からアンドゥを実行
        const undoResult = HistoryLogic.undo(activeSheet);
        if (undoResult) {
          // undoredoクラスを設定
          const setUndoRedoClassEvent = new CustomEvent('setUndoRedoClass', { 
            detail: { type: 'undo' } 
          });
          document.dispatchEvent(setUndoRedoClassEvent);
          
          // クラスが確実に適用されるのを待ってから実際のundo処理を実行
          // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              dispatch({ type: 'UPDATE_SHEET', payload: undoResult.newSheet });
              // stateRefも同期的に更新（undo後のパウチ削除等で古いデータを参照する問題を防ぐ）
              stateRef.current = {
                ...stateRef.current,
                sheets: stateRef.current.sheets.map(s => 
                  s.id === undoResult.newSheet.id ? undoResult.newSheet : s
                )
              };
              // データベースにも保存
              SheetStorage.saveSheet(undoResult.newSheet);
            });
          });
        }
      }
    };

    document.addEventListener('forceUndo', handleForceUndo);
    return () => {
      document.removeEventListener('forceUndo', handleForceUndo);
    };
  }, [activeSheet]);

  // ウィンドウIDを一度だけ生成（モジュールレベル）
  const windowId = useRef<string>('');
  if (!windowId.current) {
    windowId.current = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
  }

  // 既存ウィンドウをチェックする関数
  const checkForExistingWindows = useCallback(async (): Promise<Window[]> => {
    return new Promise((resolve) => {
      // 一度生成されたウィンドウIDを使用
      const myWindowId = windowId.current!;
      
      // より確実な方法：専用のチャンネルで一意のメッセージIDを使用
      const channelName = `pouches-window-check-${Date.now()}`;
      const channel = new BroadcastChannel(channelName);
      const checkId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      let otherWindowsFound = false;
      let hasResponded = false;
      
      
      
      const responseHandler = (event: MessageEvent) => {
        if (hasResponded) {
          
          return;
        }
        
        
        if (event.data.type === 'WINDOW_RESPONSE' && event.data.checkId === checkId) {
          
          // 自分自身からの応答でないかチェック
          if (event.data.windowId !== myWindowId) {
            
            otherWindowsFound = true;
          } else {
            
          }
          hasResponded = true;
        }
      };
      
      // 既存ウィンドウからの応答をリッスン
      const globalChannel = new BroadcastChannel('pouches-window-exists');
      globalChannel.addEventListener('message', responseHandler);
      
      // 既存ウィンドウに存在確認を送信
      
      globalChannel.postMessage({
        type: 'WINDOW_CHECK',
        checkId: checkId,
        senderWindowId: myWindowId,
        responseChannel: channelName
      });
      
      // 100ms待ってレスポンスを確認
      setTimeout(() => {
        globalChannel.removeEventListener('message', responseHandler);
        globalChannel.close();
        channel.close();
        
        
        
        if (otherWindowsFound) {
          
          resolve([window]); // ダミーウィンドウ
        } else {
          
          resolve([]);
        }
      }, 100);
    });
  }, [windowId]);

  // 既存ウィンドウにファイルを送信する関数
  const sendFileToExistingWindow = useCallback(async (file: File, targetWindow: Window): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        // ファイルをBase64に変換
        const reader = new FileReader();
        reader.onload = () => {
          const channel = new BroadcastChannel('pouches-file-import');
          const responseChannel = new BroadcastChannel('pouches-import-response');
          const importId = Date.now().toString();
          
          // 応答リスナーを設定
          const responseHandler = (event: MessageEvent) => {
            if (event.data.type === 'IMPORT_SUCCESS' && event.data.importId === importId) {
              responseChannel.removeEventListener('message', responseHandler);
              responseChannel.close();
              resolve(true);
            }
          };
          
          responseChannel.addEventListener('message', responseHandler);
          
          // ファイルデータを送信
          channel.postMessage({
            type: 'IMPORT_POUCHES_FILE',
            fileData: reader.result,
            fileName: file.name,
            lastModified: file.lastModified,
            importId: importId,
            senderWindowId: windowId.current // 送信者のウィンドウIDを追加
          });
          channel.close();
          
          // 3秒でタイムアウト
          setTimeout(() => {
            responseChannel.removeEventListener('message', responseHandler);
            responseChannel.close();
            resolve(false);
          }, 3000);
        };
        
        reader.onerror = () => {
          resolve(false);
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('ファイル送信に失敗しました:', error);
        resolve(false);
      }
    });
  }, []);

  // 統合ファイルインポート関数（ダブルクリック以外用）
  const handleUnifiedFileImport = useCallback(async (file: File, skipDuplicateCheck: boolean = false, filePath?: string) => {
    const existingWindows = await checkForExistingWindows();
    if (existingWindows.length > 0) {
      const success = await sendFileToExistingWindow(file, existingWindows[0]);
      if (!success) await importSheetFromFile(file, skipDuplicateCheck, false, filePath);
    } else {
      await importSheetFromFile(file, skipDuplicateCheck, false, filePath);
    }
  }, [checkForExistingWindows, sendFileToExistingWindow, importSheetFromFile]);

  // Electron: File → Open メニュー
  useEffect(() => {
    const electron = (window as unknown as { electron?: { onOpenDialogRequest: (cb: () => void) => () => void; showOpenDialog: () => Promise<{ canceled: boolean; filePaths: string[] }>; readFile: (p: string) => Promise<{ success: boolean; content?: string; fileName?: string }> } }).electron;
    if (!electron?.onOpenDialogRequest || !electron?.showOpenDialog || !electron?.readFile) return;
    const cleanup = electron.onOpenDialogRequest(async () => {
      try {
        const { canceled, filePaths } = await electron.showOpenDialog();
        if (!canceled && filePaths?.[0]) {
          const result = await electron.readFile(filePaths[0]);
          if (result.success && result.content) {
            const file = new File([result.content], result.fileName || 'imported.pouches', { type: 'application/pouches' });
            await handleUnifiedFileImport(file, true, filePaths[0]);
          } else {
            alert('Failed to read file.');
          }
        }
      } catch (err) {
        console.error('Open failed:', err);
        alert('Open failed.');
      }
    });
    return cleanup;
  }, [handleUnifiedFileImport]);

  // Electron: File → Import from URL メニュー
  useEffect(() => {
    const electron = (window as unknown as { electron?: { onImportUrlPromptRequest: (cb: () => void) => () => void } }).electron;
    if (!electron?.onImportUrlPromptRequest) return;
    return electron.onImportUrlPromptRequest(() => setShowImportPrompt(true));
  }, []);

  // Electron: File menu handlers
  useEffect(() => {
    const electron = (window as unknown as {
      electron?: {
        onMenuNewRequest: (cb: () => void) => () => void;
        onMenuSaveRequest: (cb: () => void) => () => void;
        onMenuSaveAsRequest: (cb: () => void) => () => void;
        onMenuShareRequest: (cb: (t: 'private' | 'public') => void) => () => void;
      };
    }).electron;
    if (!electron?.onMenuSaveRequest) return;
    const cleanups: (() => void)[] = [];
    if (electron.onMenuNewRequest) {
      cleanups.push(electron.onMenuNewRequest(() => handleCreateNewSheet()));
    }
    cleanups.push(electron.onMenuSaveRequest(() => {
      const id = stateRef.current.activeSheetId;
      if (!id) return;
      const path = activeSheetFilePathRef.current.get(id);
      executeExportAction(id, 'download', path);
    }));
    cleanups.push(electron.onMenuSaveAsRequest(() => {
      const id = stateRef.current.activeSheetId;
      if (!id) return;
      executeExportAction(id, 'download');
    }));
    cleanups.push(electron.onMenuShareRequest((shareType) => {
      const id = stateRef.current.activeSheetId;
      if (!id) return;
      handleExportSheet(id, undefined, shareType);
    }));
    return () => cleanups.forEach((c) => c());
  }, [handleCreateNewSheet, executeExportAction, handleExportSheet]);

  // ウィンドウチェックのレスポンダーを設定
  useEffect(() => {
    // 共通のウィンドウIDを使用
    const myWindowId = windowId.current!;
    
    const globalChannel = new BroadcastChannel('pouches-window-exists');
    const fileChannel = new BroadcastChannel('pouches-file-import');
    
    const handleWindowCheck = (event: MessageEvent) => {
      if (event.data.type === 'WINDOW_CHECK') {
        
        
        
        // 送信者と自分が異なるウィンドウの場合のみ応答
        if (event.data.senderWindowId !== myWindowId) {
          
          globalChannel.postMessage({
            type: 'WINDOW_RESPONSE',
            checkId: event.data.checkId,
            windowId: myWindowId
          });
          
        } else {
          
        }
      }
    };
    
    const handleFileImport = async (event: MessageEvent) => {
      if (event.data.type === 'IMPORT_POUCHES_FILE' && event.data.fileData) {
        // 送信者自身は処理をスキップ
        if (event.data.senderWindowId === myWindowId) {
          
          return;
        }
        
        
        
        try {
          
          // Base64データからファイルを復元
          const response = await fetch(event.data.fileData);
          const blob = await response.blob();
          const fileName = event.data.fileName || 'imported.pouches';
          const originalLastModified = event.data.lastModified || Date.now();
          const file = new File([blob], fileName, { 
            type: 'application/pouches',
            lastModified: originalLastModified
          });
          
          // リーダー選出: 最初に応答したウィンドウがファイルをインポート
          const leaderKey = `import_leader_${event.data.importId}`;
          const existingLeader = sessionStorage.getItem(leaderKey);
          
          if (!existingLeader) {
            // リーダーになる
            sessionStorage.setItem(leaderKey, myWindowId);
            
            await importSheetFromFile(file);
            
            // 他のウィンドウにデータ更新を通知
            const refreshChannel = new BroadcastChannel('pouches-refresh-data');
            refreshChannel.postMessage({
              type: 'REFRESH_REQUIRED',
              reason: 'leader_import_completed',
              leaderId: myWindowId
            });
            refreshChannel.close();
          } else {
            // フォロワーとしてDBから読み込み
            
            // REFRESH_REQUIREDイベントを待つ（handleRefreshRequestで処理される）
          }
          
          // ウィンドウにフォーカスを当てる
          window.focus();
          
          // 成功レスポンスを送信（リーダーのみ）
          if (event.data.importId && !existingLeader) {
            const responseChannel = new BroadcastChannel('pouches-import-response');
            responseChannel.postMessage({
              type: 'IMPORT_SUCCESS',
              importId: event.data.importId
            });
            responseChannel.close();
          }
        } catch (error) {
          console.error('BroadcastChannelでのファイルインポートに失敗しました:', error);
          
          // エラーレスポンスを送信
          if (event.data.importId) {
            const responseChannel = new BroadcastChannel('pouches-import-response');
            responseChannel.postMessage({
              type: 'IMPORT_ERROR',
              importId: event.data.importId,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
            responseChannel.close();
          }
        }
      }
    };
    
    // データリフレッシュチャンネルの設定
    const refreshChannel = new BroadcastChannel('pouches-refresh-data');
    
    const handleRefreshRequest = async (event: MessageEvent) => {
      if (event.data.type === 'REFRESH_REQUIRED') {
        
        
        // リーダーインポート完了の場合、自分がリーダーなら処理をスキップ
        if (event.data.reason === 'leader_import_completed' && event.data.leaderId === myWindowId) {
          
          return;
        }
        
        try {
          // データベースから最新のシートデータを取得
          
          const sheets = await SheetStorage.getAllSheets();
          
          const activeSheetId = SheetStorage.getActiveSheetId() || 
            (sheets.length > 0 ? sheets[0].id : '');
          
          // アプリの状態を更新
          dispatch({ 
            type: 'INIT_APP', 
            payload: { 
              sheets, 
              settings: state.settings // 現在の設定を保持
            } 
          });
          
          // アクティブシートも更新
          if (activeSheetId) {
            dispatch({ type: 'SET_ACTIVE_SHEET', payload: activeSheetId });
          }
          
          
          
          // 代替案：ページリロードが必要な場合は以下をコメントアウト
          // window.location.reload();
        } catch (error) {
          console.error('[Refresh] Failed to refresh data:', error);
        }
      }
    };
    
    globalChannel.addEventListener('message', handleWindowCheck);
    fileChannel.addEventListener('message', handleFileImport);
    refreshChannel.addEventListener('message', handleRefreshRequest);
    
    return () => {
      globalChannel.removeEventListener('message', handleWindowCheck);
      fileChannel.removeEventListener('message', handleFileImport);
      refreshChannel.removeEventListener('message', handleRefreshRequest);
      globalChannel.close();
      fileChannel.close();
      refreshChannel.close();
    };
  }, [importSheetFromFile, state.settings]);

  // ドラッグ&ドロップハンドラー
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    
    
    const files = Array.from(e.dataTransfer.files);
    
    
    // .pouchesファイルのみを処理
    for (const file of files) {
      if (file.name.endsWith('.pouches')) {
        
        await handleUnifiedFileImport(file, true); // 統合インポート関数を使用
        break; // 最初の.pouchesファイルのみ処理
      }
    }
  }, [handleUnifiedFileImport]);

  // グループを書き出すハンドラ
  const handleExportGroup = async (groupId: string) => {
    if (!activeSheet) return;
    
    // 書き出し機能を呼び出す
    try {
      const result = await shareGroupContent(
        groupId,
        activeSheet.pouches,
        activeSheet.groups,
        state.sheets,
        state.activeSheetId
      );
      
      if (result) {
      }
    } catch (error) {
      console.error('グループの書き出しに失敗しました:', error);
    }
  };

  // グループ内のパウチをすべて正負転換するハンドラ
  const handleToggleGroupSign = (groupId: string, currentPouches?: Pouch[], currentGroups?: Group[]) => {
    if (!activeSheet) return;
    
    // 最新のグループデータを使用（currentGroupsが渡されない場合は activeSheet.groups をフォールバック）
    const groupsToUse = currentGroups || activeSheet.groups;
    const group = groupsToUse.find(g => g.id === groupId);
    if (!group || !group.pouches) return;
    
    // 最新のパウチデータを使用（currentPouchesが渡されない場合は activeSheet.pouches をフォールバック）
    const pouchesToUpdate = currentPouches || activeSheet.pouches;
    
    // グループに所属するパウチをすべて正負転換
    let updatedPouches = pouchesToUpdate.map(pouch => {
      if (group.pouches?.includes(pouch.id)) {
        if (pouch.isTime && !pouch.isGroupTotalPouch) {
          const next = { ...pouch, value: -pouch.value, multiplier: pouch.multiplier };
          return {
            ...next,
            isNegative: getTimePouchEffectiveForTotal(next) < 0
          };
        }
        const newValue = -pouch.value;
        return {
          ...pouch,
          value: newValue,
          isNegative: newValue < 0
        };
      }
      return pouch;
    });

    // 帳尻パウチが有効なときは onUpdateActiveSheet を経由しないため、ここで BP を再計算（グループ外の BP 含む）
    const balancingPouchId = activeSheet.balancingPouchId ?? null;
    const balancingTargetTotal = activeSheet.balancingTargetTotal;
    if (balancingPouchId && balancingTargetTotal !== undefined) {
      const bpPouch = updatedPouches.find(p => p.id === balancingPouchId);
      if (bpPouch && !bpPouch._isDeleting) {
        updatedPouches = applyBalancingPouch(updatedPouches, groupsToUse, balancingPouchId, balancingTargetTotal);
        updatedPouches = updatedPouches.map(p => {
          if (p.id !== balancingPouchId) return p;
          if (p.isTime) {
            return { ...p, isNegative: getTimePouchEffectiveForTotal(p) < 0 };
          }
          return { ...p, isNegative: p.value < 0 };
        });
      }
    }
    
    // パウチとグループを更新
    dispatch({
      type: 'UPDATE_SHEET',
      payload: {
        ...activeSheet,
        pouches: updatedPouches,
        groups: groupsToUse
      }
    });
  };


  // 別シートのオブジェクトを直接削除する専用関数
  const deleteObjectsFromSheet = useCallback(async (
    targetSheetId: string,
    groupId: string,
    pouchIds: string[],
    containedGroupIds: string[]
  ) => {
    
    // 対象シートを取得
    const targetSheet = state.sheets.find(sheet => sheet.id === targetSheetId);
    if (!targetSheet) {
      return;
    }
    
    // パウチを削除
    const updatedPouches = targetSheet.pouches.filter(pouch => !pouchIds.includes(pouch.id));
    
    // グループを削除
    const updatedGroups = targetSheet.groups.filter(group => 
      group.id !== groupId && !containedGroupIds.includes(group.id)
    );
    
    // 更新されたシートを作成
    const updatedSheet = {
      ...targetSheet,
      pouches: updatedPouches,
      groups: updatedGroups
    };
    
    // シートを更新
    dispatch({ type: 'UPDATE_SHEET', payload: updatedSheet });
    
    // stateRefも同期的に更新
    stateRef.current = {
      ...stateRef.current,
      sheets: stateRef.current.sheets.map(s => 
        s.id === updatedSheet.id ? updatedSheet : s
      )
    };
    
    // 複数シートの保存処理を実行
    await saveMultipleSheets([updatedSheet]);
  }, [state.sheets, dispatch]);

  // 複数シートを同時に保存する関数
  const saveMultipleSheets = useCallback(async (sheetsToSave: Sheet[]) => {
    try {
      // すべてのシートを並行して保存
      await Promise.all(sheetsToSave.map(async (sheet) => {
        const result = await SheetStorage.saveSheet(sheet);
        return result;
      }));
    } catch (error) {
      // 個別に保存を試行
      for (const sheet of sheetsToSave) {
        try {
          await SheetStorage.saveSheet(sheet);
        } catch (individualError) {
          console.error('Failed to save individual sheet:', sheet.id, individualError);
        }
      }
    }
  }, []);

  // シート間ドラッグ開始処理（グループ用）
  const handleStartSheetDrag = useCallback((groupData: {
    group: Group;
    pouches: Pouch[];
    containedGroups: Group[];
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => {
    draggedGroupDataRef.current = groupData;
    sourceSheetGroupRef.current = groupData.group; // 元シートのグループ情報を保存
    setIsDraggingToSheet(true);
  }, []);

  // シート間ドラッグ開始処理（パウチ用）
  const handleStartSheetPouchDrag = useCallback((pouchData: {
    pouch: Pouch;
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => {
    draggedPouchDataRef.current = pouchData;
    sourceSheetPouchRef.current = pouchData.pouch; // 元シートのパウチ情報を保存
    setIsDraggingToSheet(true);    
  }, []);

  // シート一覧でのマウスホバー処理
  const handleSheetHover = useCallback((sheetId: string, isEntering: boolean) => {
    
    // ドラッグ中でない場合は処理しない（グループまたはパウチ）
    if (!isDraggingToSheet || (!draggedGroupDataRef.current && !draggedPouchDataRef.current)) return;
    
    if (isEntering) {
      setHoveredSheetId(sheetId);
      
      // 1.5秒後にシート切り替え
      if (sheetHoverTimerRef.current) {
        clearTimeout(sheetHoverTimerRef.current);
      }
      
      sheetHoverTimerRef.current = window.setTimeout(() => {
        if (sheetId !== state.activeSheetId) {
          // シート切り替え前にドラッグ状態を保存
          
          if (activeSheetRef.current && globalInteractionRef.current) {
            const currentComponent = activeSheetRef.current as any;
            
            // 現在のマウス/タッチ位置を取得
            const currentMousePosition = globalInteractionRef.current.currentPosition || { x: 0, y: 0 };
            
            // 現在のドラッグ状態を保存
            preservedDragStateRef.current = {
              interaction: { 
                ...globalInteractionRef.current,
                currentPosition: currentMousePosition
              },
              draggedFromOtherSheet: globalInteractionRef.current.draggedFromOtherSheet || false,
              draggedGroupId: globalInteractionRef.current.draggedGroupId,
              tempPouchPosition: currentComponent.getTempPouchPosition ? currentComponent.getTempPouchPosition() : null,
              draggedGroupData: draggedGroupDataRef.current,
              draggedPouchData: draggedPouchDataRef.current
            };
            
          }
          
          // シート切り替え前に**古い**ActiveSheetComponentのみを削除
          
          // 現在のアクティブシートのActiveSheetComponentは保護
          const currentActiveSheetId = state.activeSheetId;
          const targetSheetId = sheetId; // 移動先のシートID
          
          // 削除対象: 現在のアクティブシートでも移動先シートでもないActiveSheetComponent
          const allCalculatorAreas = document.querySelectorAll('.calculator-area');
          
          allCalculatorAreas.forEach((area, index) => {
            const parent = area.parentElement;
            if (parent) {
              // data-sheet-id属性でシートを識別
              const areaSheetId = area.getAttribute('data-sheet-id') || 'unknown';
              
              
              // 現在のアクティブシートと移動先シート以外を削除
              if (areaSheetId !== currentActiveSheetId && areaSheetId !== targetSheetId && areaSheetId !== 'unknown') {
                
                // 削除フラグを設定
                (window as any)[`destroying_${areaSheetId}`] = true;
                
                parent.style.display = 'none';
                parent.remove();
              } else {
              }
            }
          });
          
          
          // シート切り替え前に、現在のシートからシート間ドラッグで作成された要素を削除
          // ただし、ソースシート（ドラッグ開始元のシート）では実行しない
          const isSourceSheet = (draggedGroupDataRef.current?.sourceSheetId === state.activeSheetId) ||
                               (draggedPouchDataRef.current?.sourceSheetId === state.activeSheetId);
          
          // より厳密な判定：現在のシートがソースシートかつ、まだ一度もシート移動していない場合
          const isFirstMoveFromSource = isSourceSheet && !globalInteractionRef.current;
          
          
          if (activeSheetRef.current && !isFirstMoveFromSource) {
            const currentComponent = activeSheetRef.current as any;
            if (currentComponent.removeCrossSheetElements) {
              currentComponent.removeCrossSheetElements();
              
              // 1秒後にシート切り替え
              setTimeout(() => {
                handleSelectSheet(sheetId, new Event('click') as any);
                
                // シート切り替え後に内容を作成（描画完了を確実に待つ）
                const waitForRender = () => {
                  // requestAnimationFrameを複数回実行して描画完了を確実に待つ
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      // さらに少し遅延を入れて確実に描画完了を待つ
                      setTimeout(() => {
                        if (activeSheetRef.current) {
                          // まずドラッグ状態を復元（要素データも含む）
                          if (preservedDragStateRef.current) {
                            const newComponent = activeSheetRef.current as any;
                            if (newComponent.restoreDragState) {
                              newComponent.restoreDragState(preservedDragStateRef.current);
                            }
                            // グローバルドラッグ状態も復元
                            globalInteractionRef.current = preservedDragStateRef.current.interaction;
                            
                            // ドラッグ中の要素データも復元
                            if (preservedDragStateRef.current.draggedGroupData) {
                              draggedGroupDataRef.current = preservedDragStateRef.current.draggedGroupData;
                            }
                            if (preservedDragStateRef.current.draggedPouchData) {
                              draggedPouchDataRef.current = preservedDragStateRef.current.draggedPouchData;
                            }
                          }
                          
                          // グループドラッグの場合
                          if (draggedGroupDataRef.current) {
                            (activeSheetRef.current as any).createDraggedContent(draggedGroupDataRef.current);
                          }
                          // パウチドラッグの場合
                          if (draggedPouchDataRef.current) {
                            (activeSheetRef.current as any).createDraggedPouch(draggedPouchDataRef.current);
                          }
                          
                          // 保存した状態をクリア
                          if (preservedDragStateRef.current) {
                            preservedDragStateRef.current = null;
                          }
                        }
                      }, 50);
                    });
                  });
                };
                
                waitForRender();
              }, 50);
            } else {
              // removeCrossSheetElementsが実行されない場合は即座にシート切り替え
              handleSelectSheet(sheetId, new Event('click') as any);
              
              // シート切り替え後に内容を作成（描画完了を確実に待つ）
              const waitForRender = () => {
                // requestAnimationFrameを複数回実行して描画完了を確実に待つ
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    // さらに少し遅延を入れて確実に描画完了を待つ
                    setTimeout(() => {
                      if (activeSheetRef.current) {
                        // まずドラッグ状態を復元（要素データも含む）
                        if (preservedDragStateRef.current) {
                          const newComponent = activeSheetRef.current as any;
                          if (newComponent.restoreDragState) {
                            newComponent.restoreDragState(preservedDragStateRef.current);
                          }
                          // グローバルドラッグ状態も復元
                          globalInteractionRef.current = preservedDragStateRef.current.interaction;
                          
                          // ドラッグ中の要素データも復元
                          if (preservedDragStateRef.current.draggedGroupData) {
                            draggedGroupDataRef.current = preservedDragStateRef.current.draggedGroupData;
                          }
                          if (preservedDragStateRef.current.draggedPouchData) {
                            draggedPouchDataRef.current = preservedDragStateRef.current.draggedPouchData;
                          }
                        }
                        
                        // グループドラッグの場合
                        if (draggedGroupDataRef.current) {
                          (activeSheetRef.current as any).createDraggedContent(draggedGroupDataRef.current);
                        }
                        // パウチドラッグの場合
                        if (draggedPouchDataRef.current) {
                          (activeSheetRef.current as any).createDraggedPouch(draggedPouchDataRef.current);
                        }
                        
                        // 保存した状態をクリア
                        if (preservedDragStateRef.current) {
                          preservedDragStateRef.current = null;
                        }
                      }
                    }, 50);
                  });
                });
              };
              
              waitForRender();
            }
          } else {
            // 最初のシートから移動する場合は即座にシート切り替え
            handleSelectSheet(sheetId, new Event('click') as any);
            
            // シート切り替え後に内容を作成（描画完了を確実に待つ）
            const waitForRender = () => {
              // requestAnimationFrameを複数回実行して描画完了を確実に待つ
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  // さらに少し遅延を入れて確実に描画完了を待つ
                  setTimeout(() => {
                    if (activeSheetRef.current) {
                      // グループドラッグの場合
                      if (draggedGroupDataRef.current) {
                        (activeSheetRef.current as any).createDraggedContent(draggedGroupDataRef.current);
                      }
                      // パウチドラッグの場合
                      else if (draggedPouchDataRef.current) {
                        (activeSheetRef.current as any).createDraggedPouch(draggedPouchDataRef.current);
                      }
                    }
                  }, 50);
                });
              });
            };
            
            waitForRender();
          }
        }
      }, 1500);
    } else {
      if (hoveredSheetId === sheetId) {
        setHoveredSheetId(null);
        if (sheetHoverTimerRef.current) {
          clearTimeout(sheetHoverTimerRef.current);
          sheetHoverTimerRef.current = null;
        }
      }
    }
  }, [isDraggingToSheet, hoveredSheetId, state.activeSheetId, handleSelectSheet]);

  // グローバルなマウスアップイベントリスナー
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      // シート間ドラッグ状態をクリア
      if (isDraggingToSheet) {
        setTimeout(() => {
          setIsDraggingToSheet(false);
          draggedGroupDataRef.current = null;
          draggedPouchDataRef.current = null;
          setHoveredSheetId(null);
          
          if (sheetHoverTimerRef.current) {
            clearTimeout(sheetHoverTimerRef.current);
            sheetHoverTimerRef.current = null;
          }
        }, 50);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDraggingToSheet]);

  // グローバルなタッチ移動イベントリスナー（シート間移動用）
  useEffect(() => {
    const handleGlobalTouchMove = (e: TouchEvent) => {
      // ドラッグ中でない場合は処理しない
      if (!isDraggingToSheet || (!draggedGroupDataRef.current && !draggedPouchDataRef.current)) {
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      // タッチ位置にある要素を取得
      const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!elementUnderTouch) return;

      // シートタブの要素かどうかを確認
      const sheetItem = elementUnderTouch.closest('.sheet-item');
      
      if (sheetItem) {
        const sheetId = sheetItem.getAttribute('data-sheet-id');
        if (sheetId && sheetId !== hoveredSheetId) {
          // 前回のホバーをクリア
          if (hoveredSheetId) {
            handleSheetHover(hoveredSheetId, false);
          }
          // 新しいシートにホバー
          handleSheetHover(sheetId, true);
        }
      } else {
        // シートタブの外にある場合、ホバーをクリア
        if (hoveredSheetId) {
          handleSheetHover(hoveredSheetId, false);
        }
      }
    };

    const handleGlobalTouchEnd = () => {
      // シート間ドラッグ状態をクリア
      if (isDraggingToSheet) {
        setTimeout(() => {
          setIsDraggingToSheet(false);
          draggedGroupDataRef.current = null;
          draggedPouchDataRef.current = null;
          setHoveredSheetId(null);
          
          if (sheetHoverTimerRef.current) {
            clearTimeout(sheetHoverTimerRef.current);
            sheetHoverTimerRef.current = null;
          }
        }, 50);
      }
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);
    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDraggingToSheet, hoveredSheetId, handleSheetHover]);

  return (
    <GuideContext.Provider value={isGuideEnabled}>
    <>
    <div className="app-container"
      data-active-sheet-id={state.activeSheetId}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* サイドバー */}
      <div className={`left-sidebar ${state.isLeftSidebarOpen ? 'open' : 'closed'}`}>
        <div className="left-sidebar-handle" 
          onClick={toggleMenu}
          onTouchEnd={(e) => e.stopPropagation()}
          data-tooltip-id="tt"
          data-tooltip-html={formatTooltip(state.isLeftSidebarOpen ? 'Click: Close Menu & Sheet List' : 'Click: Open Menu & Sheet List')}
        >
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ 
              width: 'var(--icon-size)', 
              height: 'var(--icon-size)' 
            }}
          >
            {state.isLeftSidebarOpen ? (
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            ) : (
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </svg>
        </div>
        
        <div className="left-sidebar-header">
          <div className="app-title">
            <h1 className="main-title" data-tooltip-id="tt" data-tooltip-html={[`<b>POUCHES Ver. ${packageJson.version}</b>`, formatTooltip('Click: Reload')].filter(Boolean).join('<br/>')}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/logo.svg`} 
                alt="POUCHES" 
                style={{ cursor: 'pointer' }}
                onClick={() => window.location.reload()}
                onTouchEnd={() => window.location.reload()}
              />
            </h1>
            {/* <h2 className="sub-title">LAYOUT & CALCULATE</h2> */}
          </div>
          <button className="new-sheet-button" 
            tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
            data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Create New Sheet")}
            onClick={handleCreateNewSheet} 
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M20 12H4" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 4V20" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>New</span>
          </button>
          <button className="import-sheet-button" 
            tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
            data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Import Sheet")}
            onClick={handleImportSheet} 
            onTouchEnd={handleImportSheet}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M4 15v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 12l4 4 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 2v14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Import</span>
          </button>
        </div>

        <div className="sheet-list">
          {state.sheets.map(sheet => (
            <div 
              key={sheet.id}
              className={`sheet-item ${sheet.id === state.activeSheetId ? 'active' : ''} ${hoveredSheetId === sheet.id ? 'hovered-for-drag' : ''} ${(draggedGroupDataRef.current?.sourceSheetId === sheet.id || draggedPouchDataRef.current?.sourceSheetId === sheet.id) ? 'source-for-drag' : ''}`}
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={(e) => handleSelectSheet(sheet.id, e)}
              onTouchEnd={(e) => handleSelectSheet(sheet.id, e)}
              onMouseEnter={() => handleSheetHover(sheet.id, true)}
              onMouseLeave={() => handleSheetHover(sheet.id, false)}
              onTouchStart={(e) => {
                // タッチ開始時の処理（ドラッグ中の場合のみ）
                if (isDraggingToSheet && (draggedGroupDataRef.current || draggedPouchDataRef.current)) {
                  handleSheetHover(sheet.id, true);
                }
              }}
              data-sheet-id={sheet.id}
            >
              <div className="sheet-item-content">
                <div className="sheet-title" data-tooltip-id="tt" data-tooltip-html={(() => {
                  const titleHtml = sheet.title ? `<b>${sheet.title}</b>` : '';
                  const guideLines = sheet.id === state.activeSheetId
                    ? formatTooltip('Click: Edit Sheet Title')
                    : [formatTooltip('Click: Select Sheet'), formatTooltip('Double Click: Edit Sheet Title')].filter(Boolean).join('<br/>');
                  return [titleHtml, guideLines].filter(Boolean).join('<br/>');
                })()}>
                  <input
                    type="text"
                    placeholder="No Title"
                    tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                    value={sheet.title || ''}
                    onChange={(e) => handleSheetTitleChange(sheet.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => {
                      const target = e.currentTarget;
                      const wasFocused = document.activeElement === target;
                      if (!wasFocused) {
                        // 初回クリックの場合、フォーカス後に全選択
                        requestAnimationFrame(() => {
                          target.select();
                        });
                      }
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // モバイル環境では明示的にフォーカスを設定
                      const target = e.target as HTMLInputElement;
                      target.focus();
                      target.select();
                    }}
                    onCompositionStart={() => {
                      // IME入力開始時のフラグ設定
                      (window as any).isIMEComposing = true;
                    }}
                    onCompositionEnd={() => {
                      // IME入力終了時のフラグクリア
                      (window as any).isIMEComposing = false;
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      // IME入力中は全てのキー入力をスキップ
                      if ((window as any).isIMEComposing) {
                        return;
                      }
                      
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </div>
                <div className="sheet-total-date" data-tooltip-id="tt" data-tooltip-html={(() => {
                  const titleHtml = sheet.title ? `<b>${sheet.title}</b>` : '';
                  const sheetTotal = sheet.pouches
                    .filter(p => !p._isDeleting && !isInMuteGroup(p.id, sheet.groups))
                    .reduce((total, p) => total + getPouchEffectiveContribution(p), 0);
                  const formatted = sheetTotal !== 0 ? `<b>${(sheetTotal < 0 ? '-' : '') + Math.abs(sheetTotal).toLocaleString(undefined, { maximumFractionDigits: 8 })}</b>` : '';
                  const guideLines = sheet.id === state.activeSheetId
                    ? ''
                    : formatTooltip('Click: Select Sheet');
                  return [titleHtml, formatted, guideLines].filter(Boolean).join('<br/>');
                })()}>
                  <span className='sheet-total'>
                    {(() => {
                      const sheetTotal = sheet.pouches
                        .filter(p => !p._isDeleting && !isInMuteGroup(p.id, sheet.groups))
                        .reduce((total, p) => total + getPouchEffectiveContribution(p), 0);
                      
                      // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                      const allPouchesAreTime = sheetContributingPouchesAreAllTime(sheet.pouches, sheet.groups || []);
                      
                      if (allPouchesAreTime) {
                        // 時間表示（actualValueベース）
                        const totalMinutes = Math.round(Math.abs(sheetTotal));
                        const hours = Math.floor(totalMinutes / 60);
                        const minutes = totalMinutes % 60;
                        const sign = sheetTotal < 0 ? '-' : '';
                        return (
                          <>
                            <strong style={sheetTotal < 0 ? { color: 'var(--negative-color)' } : undefined}>{`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}</strong>
                            <NumberCounter 
                              key={`sheet-total-${sheet.id}`}
                              value={Math.abs(sheetTotal)} 
                              isNegative={sheetTotal < 0}
                              animateFromZero={isInitialLoading}
                              suppressTooltip={true}
                              duration={isInitialLoading ? 600 : !isSheetSwitchingRef.current || isDraggingToSheet ? 200 : 0}
                            />
                          </>
                        );
                      } else {
                        // 通常表示
                        return (
                          <NumberCounter 
                            key={`sheet-total-${sheet.id}`}
                            value={Math.abs(sheetTotal)} 
                            isNegative={sheetTotal < 0}
                            animateFromZero={isInitialLoading}
                            suppressTooltip={true}
                            duration={isInitialLoading ? 600 : !isSheetSwitchingRef.current || isDraggingToSheet ? 200 : 0}
                          />
                        );
                      }
                    })()}
                  </span>
                  <br/>
                  {new Date(sheet.createdAt).toLocaleString()}
                </div>
              </div>
              <button 
                className="duplicate-sheet-button"
                data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Clone Sheet")}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicateSheet(sheet.id, e);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  handleDuplicateSheet(sheet.id, e);
                }}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    width: 'var(--icon-size)', 
                    height: 'var(--icon-size)' 
                  }}
                >
                  <path d="M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="export-sheet-button"
                data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Export & Share Sheet")}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportSheet(sheet.id, e);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  handleExportSheet(sheet.id, e);
                }}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  stroke="var(--icon-color)"
                  style={{ 
                    width: 'var(--icon-size)', 
                    height: 'var(--icon-size)' 
                  }}
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 6l-4-4-4 4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2v13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="delete-sheet-button"
                data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Double Click: Delete Sheet\n${getModifierKeys().shift}+Click: Delete All Groups and Pouches`)}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSheet(sheet.id, e);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  handleDeleteSheet(sheet.id, e);
                }}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    width: 'var(--icon-size)', 
                    height: 'var(--icon-size)' 
                  }}
                >
                  <path d="M3 6h18" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  <line x1="10" y1="11" x2="10" y2="17" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></line>
                  <line x1="14" y1="11" x2="14" y2="17" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></line>
                </svg>
              </button>
            </div>
          ))}
        </div>
        
        {/* フッター */}
        <div className="left-sidebar-footer">
          <div className="snap-toggle-row">
            {/* グリッド切り替えスイッチ */}
            <div className="snap-toggle"
              data-tooltip-id="tt"
              data-tooltip-html={formatTooltip(isSnapEnabled ? 'Click: Hide Grid' : 'Click: Show Grid')}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setIsSnapEnabled(!isSnapEnabled); }}
            >
              <span className="snap-label">Grid</span>
              <label className="snap-switch">
                <input
                  type="checkbox"
                  checked={isSnapEnabled}
                  onChange={(e) => setIsSnapEnabled(e.target.checked)}
                  tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                />
                <span className="snap-slider"></span>
              </label>
            </div>

            {/* ガイド切り替えスイッチ */}
            <div className="snap-toggle guide-toggle"
              data-tooltip-id="tt"
              data-tooltip-html={`${formatTooltip('Click')} <span class="tt-desc">${isGuideEnabled ? 'Hide Tooltip Guide' : 'Show Tooltip Guide'}</span>`}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); (window as any).__isGuideEnabled = !isGuideEnabled; setIsGuideEnabled(!isGuideEnabled); }}
            >
              <span className="snap-label">Guide</span>
              <label className="snap-switch">
                <input
                  type="checkbox"
                  checked={isGuideEnabled}
                  onChange={(e) => {
                    (window as any).__isGuideEnabled = e.target.checked;
                    setIsGuideEnabled(e.target.checked);
                  }}
                  tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
                />
                <span className="snap-slider"></span>
              </label>
            </div>
          </div>
          
          <p className="help-link">
            <button 
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={() => openExternal('https://apps.tomippe.jp/pouches/guide-mini.pdf')} 
              onTouchEnd={(e) => { e.stopPropagation(); openExternal('https://apps.tomippe.jp/pouches/guide-mini.pdf'); }}
              data-tooltip-id="tt"
              data-tooltip-html={formatTooltip('Click: Open Quick Help (PDF)')}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Quick Help
            </button>
            <button 
              tabIndex={state.isLeftSidebarOpen ? 3000 : -1}
              onClick={() => openExternal('https://apps.tomippe.jp/pouches/guide.pdf')} 
              onTouchEnd={(e) => { e.stopPropagation(); openExternal('https://apps.tomippe.jp/pouches/guide.pdf'); }}
              data-tooltip-id="tt"
              data-tooltip-html={formatTooltip('Click: Open Instruction (PDF)')}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Instruction
            </button></p>
          <p className="apps-logo"><a href="https://apps.tomippe.jp/" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); openExternal('https://apps.tomippe.jp/'); }} onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); openExternal('https://apps.tomippe.jp/'); }} data-tooltip-id="tt" data-tooltip-html={formatTooltip('Click: Open Apps Site (External)')}>
            <img src={`${process.env.PUBLIC_URL || ''}/assets/apps-logo.svg`} alt="StudioTomippe Apps" />
          </a></p>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div 
        className="main-content"
      >
        {/* アクティブシートのタイトル入力欄 */}
        {activeSheet && (
          <div className="active-sheet-title">
            <input
              ref={activeSheetTitleInputRef}
              type="text"
              placeholder="No Title"
              tabIndex={1000}
              data-tooltip-id="tt"
              data-tooltip-html={isActiveSheetTitleFocused ? activeSheetTitleEditTooltipHtml : formatTooltip('Click: Edit Sheet Title')}
              value={activeSheet.title || ''}
              onFocus={() => setIsActiveSheetTitleFocused(true)}
              onBlur={() => setIsActiveSheetTitleFocused(false)}
              onChange={(e) => handleSheetTitleChange(activeSheet.id, e.target.value)}
              onMouseDown={(e) => {
                const target = e.currentTarget;
                const wasFocused = document.activeElement === target;
                if (!wasFocused) {
                  // 初回クリックの場合、フォーカス後に全選択
                  requestAnimationFrame(() => {
                    target.select();
                  });
                }
              }}
              onTouchEnd={(e) => {
                // モバイル環境では明示的にフォーカスを設定
                const target = e.target as HTMLInputElement;
                target.focus();
                target.select();
              }}
              onCompositionStart={() => {
                // IME入力開始時のフラグ設定
                (window as any).isIMEComposing = true;
              }}
              onCompositionEnd={() => {
                // IME入力終了時のフラグクリア
                (window as any).isIMEComposing = false;
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                // IME入力中は全てのキー入力をスキップ
                if ((window as any).isIMEComposing) {
                  return;
                }
                
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
          </div>
        )}
        {activeSheet ? (
          <ActiveSheetComponent
            key={state.activeSheetId} // シート切り替え時にコンポーネントを再マウント
            ref={activeSheetRef}
            activeSheetId={state.activeSheetId}
            lastPouchValue={activeSheet.lastPouchValue || 0}
            lastPouchColor={activeSheet.lastPouchColor || POUCH_COLORS[0].id}
            lastGroupColor={activeSheet.lastGroupColor || GROUP_COLORS[0].id}
            initialPouches={activeSheet.pouches}
            initialGroups={activeSheet.groups}
            onUpdateActiveSheet={handleUpdateActiveSheet}
            onUpdateStatistics={handleUpdateStatistics}
            defaultValue={state.settings.defaultValue}
            globalInteractionRef={globalInteractionRef}
            onRemoveGroupFromSourceSheet={removeGroupFromSourceSheet}
            sourceSheetGroupRef={sourceSheetGroupRef}
            draggedGroupDataRef={draggedGroupDataRef}
            onStartSheetPouchDrag={handleStartSheetPouchDrag}
            onRemovePouchFromSourceSheet={removePouchFromSourceSheet}
            sourceSheetPouchRef={sourceSheetPouchRef}
            draggedPouchDataRef={draggedPouchDataRef}
            onResetSheetDragState={resetSheetDragState}
            isInitialLoading={isInitialLoading}
            colorLegends={activeSheet.colorLegends || []}
            isTouchDevice={state.isTouchDevice}
            onUpdateDeviceType={handleUpdateDeviceType}
            onExportGroup={handleExportGroup}
            onToggleGroupSign={handleToggleGroupSign}
            currentSheet={activeSheet}
            onToggleBalancingPouch={handleToggleBalancingPouch}
            isDraggingToSheet={isDraggingToSheet}
            onStartSheetDrag={handleStartSheetDrag}
            onRequestFocusSheetTitle={() => {
              const el = activeSheetTitleInputRef.current;
              if (el) {
                el.focus();
                el.select();
              }
            }}
            isSheetTitleEditing={isActiveSheetTitleFocused}
            onTableHoverChange={(groupId, pouchId) => {
              // 前回の状態と同じ場合は何もしない
              const lastState = lastHoverStateRef.current;
              if (lastState.groupId === groupId && lastState.pouchId === pouchId) {
                return;
              }
              
              // 前回の状態を更新
              lastHoverStateRef.current = {groupId, pouchId};
              
              // 計算エリアからのhover状態をテーブルに反映
              // まず既存のhoverクラスをクリア
              document.querySelectorAll('.hover').forEach(el => {
                el.classList.remove('hover');
              });
              
              // 新しいhover状態を適用
              if (groupId) {
                // グループの場合、グループヘッダー行のみにhoverクラスを付与
                const groupRows = document.querySelectorAll(`tr[data-group-id="${groupId}"]:not([data-pouch-id])`);
                groupRows.forEach(el => {
                  el.classList.add('hover');
                });
              } else if (pouchId) {
                // パウチの場合、パウチ行にhoverクラスを付与
                const pouchRows = document.querySelectorAll(`tr[data-pouch-id="${pouchId}"]`);
                pouchRows.forEach(el => {
                  el.classList.add('hover');
                });
              }
            }}
          />
        ) : (
          <div className="empty-state">
            <p>Select a sheet or create a new sheet</p>
          </div>
        )}
        
        <div className="footer">
          {/* 凡例コンポーネント */}
          <LegendsComponent
            pouches={activeSheet?.pouches || []}
            colorLegends={activeSheet?.colorLegends || []}
            activeSheetId={state.activeSheetId}
            isSheetSwitching={isSheetSwitchingRef.current}
            onUpdateColorLegend={handleUpdateColorLegend}
            calculateColorTotal={handleCalculateColorTotal}
            calculateColorCount={handleCalculateColorCount}
            onEditConfirm={handleEditConfirm}
            onLegendHoverChange={(colorId) => {
              // 凡例ホバー状態の変更をActiveSheetComponentに伝える
              if (activeSheetRef.current) {
                // まず既存のlegend-hoveredクラスをクリア
                document.querySelectorAll('.legend-hovered').forEach(el => {
                  el.classList.remove('legend-hovered');
                });
                
                // 色IDが指定されている場合、該当する色のパウチとテーブル行にクラスを追加
                if (colorId) {
                  // パウチコンテナにクラスを追加
                  document.querySelectorAll(`.pouch-container[data-pouch-id]`).forEach(element => {
                    const pouchId = element.getAttribute('data-pouch-id');
                    if (pouchId) {
                      const pouch = activeSheet?.pouches.find(p => p.id === pouchId);
                      if (pouch && pouch.color === colorId) {
                        element.classList.add('legend-hovered');
                      }
                    }
                  });
                  
                  // テーブル行（tr）にクラスを追加
                  document.querySelectorAll(`tr[data-pouch-id]`).forEach(element => {
                    const pouchId = element.getAttribute('data-pouch-id');
                    if (pouchId) {
                      const pouch = activeSheet?.pouches.find(p => p.id === pouchId);
                      if (pouch && pouch.color === colorId) {
                        element.classList.add('legend-hovered');
                      }
                    }
                  });
                }
              }
            }}
          />
          
          <div 
            className={`total-container ${isTotalExpanded ? 'expanded' : ''}`}
            onClick={() => setIsTotalExpanded(!isTotalExpanded)}
            onTouchEnd={(e) => {
              // e.stopPropagation();
              setIsTotalExpanded(!isTotalExpanded);
            }}
          >
            <span className="total-toggle-icon">
              <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M10 7l5 5-5 5z" />
              </svg>
            </span>
            <div className="total-item">
              <div className={`total-display${activeSheet?.balancingPouchId ? ' balancing-active' : ''}`}
                data-tooltip-id="tt"
                data-tooltip-html={(() => {
                  const isBp = !!(activeSheet?.balancingPouchId && activeSheet.balancingTargetTotal !== undefined);
                  if (isBp) {
                    const target = activeSheet!.balancingTargetTotal!;
                    const diff = snapBalancingTotalDiff(stats.total, target);
                    const sign = diff >= 0 ? '+ ' : '- ';
                    return `<b>${formatDetailedNumber(target)} ${sign}${formatDetailedNumber(Math.abs(diff))} = ${formatDetailedNumber(stats.total)}</b>`;
                  }
                  return `<b>${formatDetailedNumber(stats.total)}</b>`;
                })()}
                onMouseEnter={() => {
                  if (activeSheet?.balancingPouchId) {
                    document.querySelectorAll(`[data-pouch-id="${activeSheet.balancingPouchId}"]`).forEach(el => el.classList.add('balancing-pouch-hovered'));
                  }
                }}
                onMouseLeave={() => {
                  document.querySelectorAll('.balancing-pouch-hovered').forEach(el => el.classList.remove('balancing-pouch-hovered'));
                }}
              >
                <span>TOTAL<svg className={`balancing-lock-icon${activeSheet?.balancingPouchId ? ' visible' : ''}`} viewBox="0 0 24 24" width="20" height="20" fill="currentColor"
                  data-tooltip-id="tt"
                  data-tooltip-html={activeSheet?.balancingPouchId && activeSheet.balancingTargetTotal !== undefined ? (() => {
                    const target = activeSheet.balancingTargetTotal!;
                    const diff = snapBalancingTotalDiff(stats.total, target);
                    const sign = diff >= 0 ? '+ ' : '- ';
                    const info = `<b>${formatDetailedNumber(target)} ${sign}${formatDetailedNumber(Math.abs(diff))} = ${formatDetailedNumber(stats.total)}</b>`;
                    const guide = formatTooltip('Click: Unlock Total & Disable Balancing');
                    return guide ? `${info}<br/>${guide}` : info;
                  })() : undefined}
                  onClick={(e) => { e.stopPropagation(); if (activeSheet?.balancingPouchId) handleToggleBalancingPouch(activeSheet.balancingPouchId); }}><path d="M16 11V7A4 4 0 0 0 8 7V11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/><rect x="5" y="11" width="14" height="10" rx="1.5"/></svg></span> 
                {(() => {
                  const isBpActive = !!(activeSheet?.balancingPouchId && activeSheet.balancingTargetTotal !== undefined);
                  const bpDiff = isBpActive ? snapBalancingTotalDiff(stats.total, activeSheet!.balancingTargetTotal!) : 0;
                  const displayValue = isBpActive ? activeSheet!.balancingTargetTotal! : stats.total;

                  const allPouchesAreTime = !!(activeSheet?.pouches && sheetContributingPouchesAreAllTime(activeSheet.pouches, activeSheet.groups || []));
                  
                  const bpTooltipHtml = isBpActive ? (() => {
                    const t = activeSheet!.balancingTargetTotal!;
                    const d = bpDiff;
                    const s = d >= 0 ? '+ ' : '- ';
                    return `<b>${formatDetailedNumber(t)} ${s}${formatDetailedNumber(Math.abs(d))} = ${formatDetailedNumber(stats.total)}</b>`;
                  })() : undefined;

                  const totalCounter = (
                    <NumberCounter 
                      key={`total-${state.activeSheetId}`}
                      value={Math.abs(displayValue)} 
                      isNegative={displayValue < 0}
                      animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                      duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                      suppressTooltip={!!bpTooltipHtml}
                    />
                  );
                  const diffSmall = bpDiff !== 0 ? <small className={`balancing-diff${displayValue < 0 ? ' negative' : ''}`}>{bpDiff > 0 ? '+' : ''}{bpDiff}</small> : null;

                  if (allPouchesAreTime) {
                    const totalMinutes = Math.round(Math.abs(displayValue));
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    const sign = displayValue < 0 ? '-' : '';
                    return (
                      <span {...(bpTooltipHtml ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': bpTooltipHtml } : {})}>
                        <strong style={displayValue < 0 ? { color: 'var(--negative-color)' } : undefined}>{`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}</strong>
                        {totalCounter}{diffSmall}
                      </span>
                    );
                  } else {
                    return (
                      <span {...(bpTooltipHtml ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': bpTooltipHtml } : {})}>
                        {totalCounter}{diffSmall}
                      </span>
                    );
                  }
                })()}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-display">
                <span>COUNT</span> <NumberCounter 
                  key={`count-${state.activeSheetId}`}
                  value={stats.count}
                  animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                  duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                />
              </div>
            </div>
            <div className="stat-item">
              <div className='stat-display'>
                <span>AVERAGE</span> 
                {(() => {
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = !!(activeSheet?.pouches && sheetContributingPouchesAreAllTime(activeSheet.pouches, activeSheet.groups || []));
                  
                  if (allPouchesAreTime) {
                    // 時間表示（actualValueベース）
                    const totalMinutes = Math.round(Math.abs(stats.average));
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    const sign = stats.average < 0 ? '-' : '';
                    return (
                      <>
                        <strong style={stats.average < 0 ? { color: 'var(--negative-color)' } : undefined}>{`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}</strong>
                        <NumberCounter 
                          key={`average-${state.activeSheetId}`}
                          value={Math.abs(stats.average)} 
                          isNegative={stats.average < 0}
                          animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                          duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                        />
                      </>
                    );
                  } else {
                    // 通常表示
                    return (
                      <NumberCounter 
                        key={`average-${state.activeSheetId}`}
                        value={Math.abs(stats.average)} 
                        isNegative={stats.average < 0}
                        animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                        duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                      />
                    );
                  }
                })()}
              </div>
            </div>
            <div 
              className="stat-item"
              onMouseEnter={() => {
                // MAX値と同じ値を持つパウチとテーブル行にクラスを追加
                if (activeSheet?.pouches && activeSheet.pouches.length > 0) {
                  // まず既存のmax-hoveredクラスをクリア
                  document.querySelectorAll('.max-hovered').forEach(el => {
                    el.classList.remove('max-hovered');
                  });
                  
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = sheetContributingPouchesAreAllTime(activeSheet.pouches, activeSheet.groups || []);
                  
                  // MAX値と同じ値を持つパウチを探す
                  activeSheet.pouches.forEach(pouch => {
                    let pouchValue;
                    if (allPouchesAreTime) {
                      // 時間パウチの場合はactualValue（分数）で比較
                      pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                    } else {
                      // 通常の場合は掛け算モードのbaseValue、割り算モードのeffectiveValueで比較
                      if (pouch.multiplier < 0) {
                        pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                      } else {
                        pouchValue = pouch.value;
                      }
                    }
                    
                    // 値が一致する場合（浮動小数点の誤差を考慮）
                    if (Math.abs(pouchValue - stats.max) < 0.0000001) {
                      // パウチコンテナにクラスを追加
                      const pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
                      if (pouchElement) {
                        pouchElement.classList.add('max-hovered');
                      }
                      
                      // テーブル行にクラスを追加
                      const rowElement = document.querySelector(`tr[data-pouch-id="${pouch.id}"]`);
                      if (rowElement) {
                        rowElement.classList.add('max-hovered');
                      }
                    }
                  });
                }
              }}
              onMouseLeave={() => {
                // max-hoveredクラスをクリア
                document.querySelectorAll('.max-hovered').forEach(el => {
                  el.classList.remove('max-hovered');
                });
              }}
            >
              <div className='stat-display'>
                <span>MAX</span> 
                {(() => {
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = !!(activeSheet?.pouches && sheetContributingPouchesAreAllTime(activeSheet.pouches, activeSheet.groups || []));
                  
                  if (allPouchesAreTime) {
                    // 時間表示（actualValueベース）
                    const totalMinutes = Math.round(Math.abs(stats.max));
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    const sign = stats.max < 0 ? '-' : '';
                    return (
                      <>
                        <strong style={stats.max < 0 ? { color: 'var(--negative-color)' } : undefined}>{`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}</strong>
                        <NumberCounter 
                          key={`max-${state.activeSheetId}`}
                          value={Math.abs(stats.max)} 
                          isNegative={stats.max < 0}
                          animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                          duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                        />
                      </>
                    );
                  } else {
                    // 通常表示
                    return (
                      <NumberCounter 
                        key={`max-${state.activeSheetId}`}
                        value={Math.abs(stats.max)} 
                        isNegative={stats.max < 0}
                        animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                        duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                      />
                    );
                  }
                })()}
              </div>
            </div>
            <div 
              className="stat-item"
              onMouseEnter={() => {
                // MIN値と同じ値を持つパウチとテーブル行にクラスを追加
                if (activeSheet?.pouches && activeSheet.pouches.length > 0) {
                  // まず既存のmin-hoveredクラスをクリア
                  document.querySelectorAll('.min-hovered').forEach(el => {
                    el.classList.remove('min-hovered');
                  });
                  
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = sheetContributingPouchesAreAllTime(activeSheet.pouches, activeSheet.groups || []);
                  
                  // MIN値と同じ値を持つパウチを探す
                  activeSheet.pouches.forEach(pouch => {
                    let pouchValue;
                    if (allPouchesAreTime) {
                      // 時間パウチの場合はactualValue（分数）で比較
                      pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                    } else {
                      // 通常の場合は掛け算モードのbaseValue、割り算モードのeffectiveValueで比較
                      if (pouch.multiplier < 0) {
                        pouchValue = getPouchActualValue(pouch, activeSheet.groups || []);
                      } else {
                        pouchValue = pouch.value;
                      }
                    }
                    
                    // 値が一致する場合（浮動小数点の誤差を考慮）
                    if (Math.abs(pouchValue - stats.min) < 0.0000001) {
                      // パウチコンテナにクラスを追加
                      const pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
                      if (pouchElement) {
                        pouchElement.classList.add('min-hovered');
                      }
                      
                      // テーブル行にクラスを追加
                      const rowElement = document.querySelector(`tr[data-pouch-id="${pouch.id}"]`);
                      if (rowElement) {
                        rowElement.classList.add('min-hovered');
                      }
                    }
                  });
                }
              }}
              onMouseLeave={() => {
                // min-hoveredクラスをクリア
                document.querySelectorAll('.min-hovered').forEach(el => {
                  el.classList.remove('min-hovered');
                });
              }}
            >
              <div className='stat-display'>
                <span>MIN</span> 
                {(() => {
                  // シートの全パウチが時間の場合の判定（GTP は参照先が全て時間なら含む）
                  const allPouchesAreTime = !!(activeSheet?.pouches && sheetContributingPouchesAreAllTime(activeSheet.pouches, activeSheet.groups || []));
                  
                  if (allPouchesAreTime) {
                    // 時間表示（actualValueベース）
                    const totalMinutes = Math.round(Math.abs(stats.min));
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    const sign = stats.min < 0 ? '-' : '';
                    return (
                      <>
                        <strong style={stats.min < 0 ? { color: 'var(--negative-color)' } : undefined}>{`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}</strong>
                        <NumberCounter 
                          key={`min-${state.activeSheetId}`}
                          value={Math.abs(stats.min)} 
                          isNegative={stats.min < 0}
                          animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                          duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                        />
                      </>
                    );
                  } else {
                    // 通常表示
                    return (
                      <NumberCounter 
                        key={`min-${state.activeSheetId}`}
                        value={Math.abs(stats.min)} 
                        isNegative={stats.min < 0}
                        animateFromZero={isInitialLoading || isSheetSwitchingRef.current}
                        duration={isInitialLoading || isSheetSwitchingRef.current ? 600 : 300}
                      />
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 右サイドバー */}
      <div className={`right-sidebar ${state.isRightSidebarOpen ? 'open' : 'closed'}`}>
        <div 
          className="right-sidebar-handle"
          onClick={toggleSidebar}
          onTouchEnd={toggleSidebar}
          data-tooltip-id="tt"
          data-tooltip-html={formatTooltip(state.isRightSidebarOpen ? 'Click: Close History & Table' : 'Click: Open History & Table')}
        >
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ 
              width: 'var(--icon-size)', 
              height: 'var(--icon-size)' 
            }}
          >
            {state.isRightSidebarOpen ? (
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            ) : (
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </svg>
        </div>
        
        {/* 履歴パネル - 常にレンダリング */}
        {activeSheet && (
            <div className="history">
              <HistoryComponent
                sheet={activeSheet}
                pouches={activeSheet.pouches}
                groups={activeSheet.groups}
                onStateUpdate={handleHistoryStateUpdate}
                isOpen={state.isRightSidebarOpen}
              />
            </div>
        )}
        
        {/* テーブル表示 - 開いているときまたは完全に閉じていないときのみレンダリング */}
        <div className="table">
          {activeSheet && (state.isRightSidebarOpen || !state.isRightSidebarFullyClosed) && (
              <TableComponent 
              isOpen={state.isRightSidebarOpen}
              onToggle={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
              groups={activeSheet.groups}
              pouches={activeSheet.pouches}
              colorLegends={activeSheet.colorLegends || []}
              currentSheet={activeSheet}
              onHoverGroupChange={(groupId, pouchId) => {
                // ホバー状態の変更をActiveSheetComponentに伝える
                if (activeSheetRef.current) {
                  // まず既存のホバー状態をクリア
                  document.querySelectorAll('.table-hovered').forEach(el => {
                    el.classList.remove('table-hovered');
                  });
                  
                  // グループの場合
                  if (groupId) {
                    // data-group-id属性を持つすべての要素を検索
                    document.querySelectorAll(`[data-group-id="${groupId}"]`).forEach(el => {
                      el.classList.add('table-hovered');
                    });
                  }
                  
                  // パウチの場合
                  if (pouchId) {
                    // data-pouch-id属性を持つすべての要素を検索
                    document.querySelectorAll(`[data-pouch-id="${pouchId}"]`).forEach(el => {
                      el.classList.add('table-hovered');
                    });
                  }
                }
              }}
              onSelectionChange={(groupIds, pouchIds) => {
                // テーブル選択状態の変更をActiveSheetComponentに伝える
                if (activeSheetRef.current) {
                  // まず既存の選択状態をクリア
                  document.querySelectorAll('.table-selected').forEach(el => {
                    el.classList.remove('table-selected');
                  });
                  
                  // グループの選択状態を更新
                  for (const groupId of groupIds) {
                    // グループIDが一致する要素を選択
                    document.querySelectorAll(`.group-wrapper[data-group-id="${groupId}"]`).forEach(el => {
                      el.classList.add('table-selected');
                    });
                  }
                  
                  // パウチの選択状態を更新
                  for (const pouchId of pouchIds) {
                    // パウチIDが一致する要素を選択
                    document.querySelectorAll(`.pouch-container[data-pouch-id="${pouchId}"]`).forEach(el => {
                      el.classList.add('table-selected');
                    });
                  }
                }
              }}
              onUpdateGroupTitle={async (groupId, title) => {
                // グループタイトルを更新
                if (activeSheet) {
                  const updatedGroups = activeSheet.groups.map(group => 
                    group.id === groupId ? { ...group, title } : group
                  );
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(null, updatedGroups, false, true, undefined, HistoryActionType.EDIT_GROUP_TITLE);
                }
              }}
              onUpdatePouchTitle={async (pouchId, title) => {
                // パウチタイトルを更新
                if (activeSheet) {
                  const updatedPouches = activeSheet.pouches.map(pouch => 
                    pouch.id === pouchId ? { ...pouch, title } : pouch
                  );
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, HistoryActionType.EDIT_POUCH_TITLE);
                }
              }}
              onUpdatePouchValue={async (pouchId, value, multiplier?, historyActionType?, forceNewEntry?, lastExpression?, valueWhenLastExpression?, multiplyWhenLastExpression?, isTime?: boolean) => {
                // パウチ値を更新
                if (activeSheet) {
                  const updatedPouches = activeSheet.pouches.map(pouch => {
                    if (pouch.id === pouchId) {
                      // GTPの場合は通常のパウチに変換（計算エリアのマージ処理と同じロジック）
                      if (pouch.isGroupTotalPouch && historyActionType === HistoryActionType.EDIT_POUCH_VALUE) {
                        return {
                          ...pouch,
                          value,
                          multiplier: multiplier ?? pouch.multiplier,
                          isNegative: value < 0,
                          isTime: isTime !== undefined ? isTime : pouch.isTime, // 数式処理の結果でisTimeが渡された場合はそれを使用、そうでなければ現在の値を保持
                          // GTPフラグをクリア
                          isGroupTotalPouch: false,
                          referencedGroupId: undefined,
                          oppositeToTotal: undefined,
                          lastExpression: lastExpression ?? pouch.lastExpression,
                          valueWhenLastExpression: valueWhenLastExpression ?? pouch.valueWhenLastExpression,
                          multiplyWhenLastExpression: multiplyWhenLastExpression ?? pouch.multiplyWhenLastExpression
                        };
                      }
                      
                      return {
                        ...pouch,
                        value,
                        multiplier: multiplier ?? pouch.multiplier,
                        isNegative: value < 0, // 負の値の場合はisNegativeを更新
                        isTime: isTime !== undefined ? isTime : pouch.isTime, // 数式処理の結果でisTimeが渡された場合はそれを使用、そうでなければ現在の値を保持
                        lastExpression: lastExpression ?? pouch.lastExpression,
                        valueWhenLastExpression: valueWhenLastExpression ?? pouch.valueWhenLastExpression,
                        multiplyWhenLastExpression: multiplyWhenLastExpression ?? pouch.multiplyWhenLastExpression
                      };
                    }
                    return pouch;
                  });
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, historyActionType ?? HistoryActionType.EDIT_POUCH_VALUE, forceNewEntry);
                }
              }}
              onUpdatePouchMultiplier={async (pouchId, multiplier) => {
                // パウチマルチプライヤーを更新
                if (activeSheet) {
                  const updatedPouches = activeSheet.pouches.map(pouch => 
                    pouch.id === pouchId ? { ...pouch, multiplier } : pouch
                  );
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, HistoryActionType.EDIT_POUCH_VALUE);
                }
              }}
              onSetPouchError={(pouchId, hasError) => {
                // ActiveSheetComponentを通じてエラー状態を管理
                if (activeSheetRef.current) {
                  activeSheetRef.current.setError(pouchId, hasError);
                }
              }}
              onBulkUpdateTitles={async (updates) => {
                // 一括でタイトルをクリア
                if (activeSheet) {
                  let updatedGroups = activeSheet.groups;
                  let updatedPouches = activeSheet.pouches;
                  
                  // グループタイトルをクリア
                  if (updates.groupIds.length > 0) {
                    updatedGroups = activeSheet.groups.map(group => 
                      updates.groupIds.includes(group.id) ? { ...group, title: '' } : group
                    );
                  }
                  
                  // パウチタイトルをクリア
                  if (updates.pouchIds.length > 0) {
                    updatedPouches = activeSheet.pouches.map(pouch => 
                      updates.pouchIds.includes(pouch.id) ? { ...pouch, title: '' } : pouch
                    );
                  }
                  
                  // 一度に保存（グループとパウチの両方に変更がある場合も対応）
                  await handleUpdateActiveSheet(
                    updates.pouchIds.length > 0 ? updatedPouches : null,
                    updates.groupIds.length > 0 ? updatedGroups : null,
                    false,
                    true,
                    undefined,
                    updates.groupIds.length > 0 ? HistoryActionType.EDIT_GROUP_TITLE : HistoryActionType.EDIT_POUCH_TITLE
                  );
                }
              }}
              onBulkUpdate={async (updates) => {
                // 一括で複数のプロパティを更新
                if (activeSheet) {
                  let updatedGroups = activeSheet.groups;
                  let updatedPouches = activeSheet.pouches;
                  
                  // 完全なシートデータが渡された場合は直接使用
                  if ((updates as any).completeGroups) {
                    updatedGroups = (updates as any).completeGroups;
                  }
                  if ((updates as any).completePouches) {
                    updatedPouches = (updates as any).completePouches;
                    
                    // ペースト履歴タイプを取得（デフォルトはEDIT_POUCH_VALUE）
                    const pasteHistoryType = (updates as any).pasteHistoryType || HistoryActionType.EDIT_POUCH_VALUE;
                    
                    // 一度に保存（ペースト時は常に追加モード）
                    await handleUpdateActiveSheet(
                      updatedPouches,
                      updatedGroups,
                      false,
                      true,
                      undefined,
                      pasteHistoryType,
                      true // forceNewEntry: ペースト時は常に新しい履歴エントリを作成
                    );
                    return;
                  }
                  
                  // 削除処理を最初に実行
                  if (updates.deletedGroups && updates.deletedGroups.length > 0) {
                    updatedGroups = activeSheet.groups.filter(group => !updates.deletedGroups!.includes(group.id));
                  }
                  
                  if (updates.deletedPouches && updates.deletedPouches.length > 0) {
                    updatedPouches = activeSheet.pouches.filter(pouch => !updates.deletedPouches!.includes(pouch.id));
                  }
                  
                  // 削除処理がある場合はアニメーションフラグを適用
                  if ((updates.deletedGroups && updates.deletedGroups.length > 0) || 
                      (updates.deletedPouches && updates.deletedPouches.length > 0)) {
                    const animationResult = HistoryLogic.applyAnimationFlags(
                      activeSheet.pouches,
                      updatedPouches,
                      activeSheet.groups,
                      updatedGroups
                    );
                    
                    // アニメーション用の状態を即座に更新（削除フラグ付き）
                    // ActiveSheetComponentに直接アニメーション状態を渡す
                    if (activeSheetRef.current) {
                      activeSheetRef.current.setPouches(animationResult.pouches);
                      activeSheetRef.current.setGroups(animationResult.groups);
                    }
                    
                    // 300ms後に最終状態に更新（削除フラグを除去）
                    setTimeout(() => {
                      const cleanFinalPouches = updatedPouches.map(p => {
                        const cleanPouch = { ...p };
                        const pouchAny = cleanPouch as any;
                        delete pouchAny._isDeleting;
                        delete pouchAny._animateFromZero;
                        return cleanPouch;
                      });
                      
                      const cleanFinalGroups = updatedGroups.map(g => {
                        const cleanGroup = { ...g };
                        const groupAny = cleanGroup as any;
                        delete groupAny._isDeleting;
                        delete groupAny._isAdding;
                        return cleanGroup;
                      });
                      
                      // 最終状態でhandleUpdateActiveSheetを呼び出し
                      handleUpdateActiveSheet(
                        cleanFinalPouches,
                        cleanFinalGroups,
                        false,
                        true,
                        undefined,
                        HistoryActionType.DELETE_GROUP // 削除の履歴アクションタイプ
                      );
                    }, 300);
                    
                    return; // ここで処理を終了
                  }
                  
                  // グループタイトルを更新
                  if (updates.groupTitles && updates.groupTitles.length > 0) {
                    updatedGroups = updatedGroups.map(group => {
                      const groupUpdate = updates.groupTitles!.find(gt => gt.groupId === group.id);
                      return groupUpdate ? { ...group, title: groupUpdate.title } : group;
                    });
                  }
                  
                  // パウチ関連の更新を統合処理
                  const pouchUpdates = new Map<string, Partial<Pouch>>();
                  
                  // パウチタイトルを更新
                  if (updates.pouchTitles && updates.pouchTitles.length > 0) {
                    updates.pouchTitles.forEach(update => {
                      pouchUpdates.set(update.pouchId, { ...pouchUpdates.get(update.pouchId), title: update.title });
                    });
                  }
                  
                  // パウチ値を更新
                  if (updates.pouchValues && updates.pouchValues.length > 0) {
                    updates.pouchValues.forEach(update => {
                      const currentPouch = updatedPouches.find(p => p.id === update.pouchId);
                      const updateData = { 
                        ...pouchUpdates.get(update.pouchId), 
                        value: update.value, 
                        multiplier: update.multiplier,
                        isNegative: update.value < 0,
                        lastExpression: update.lastExpression,
                        valueWhenLastExpression: update.valueWhenLastExpression,
                        multiplyWhenLastExpression: update.multiplyWhenLastExpression
                      };
                      // _animateSignChangeフラグを保持
                      if ((update as any)._animateSignChange) {
                        (updateData as any)._animateSignChange = true;
                      }
                      
                      pouchUpdates.set(update.pouchId, updateData);
                    });
                  }
                  
                  // マルチプライヤーを更新
                  if (updates.pouchMultipliers && updates.pouchMultipliers.length > 0) {
                    updates.pouchMultipliers.forEach(update => {
                      pouchUpdates.set(update.pouchId, { ...pouchUpdates.get(update.pouchId), multiplier: update.multiplier });
                    });
                  }
                  
                  // パウチ更新を適用
                  if (pouchUpdates.size > 0) {
                    updatedPouches = updatedPouches.map(pouch => {
                      const pouchUpdate = pouchUpdates.get(pouch.id);
                      if (pouchUpdate) {
                        const result = { ...pouch, ...pouchUpdate };
                        return result;
                      }
                      return pouch;
                    });
                  }
                  
                  // 一度に保存
                  await handleUpdateActiveSheet(
                    updatedPouches,
                    updatedGroups,
                    false,
                    true,
                    undefined,
                    HistoryActionType.EDIT_POUCH_VALUE // デフォルトの履歴アクションタイプ
                  );
                }
              }}
              onChangeColor={async (pouchId, isShiftPressed = false) => {
                // テーブルからの色変更処理
                if (activeSheet) {
                  // 色を変更するパウチを見つける
                  const targetPouch = activeSheet.pouches.find(p => p.id === pouchId);
                  if (!targetPouch) return;
                  
                  // 現在の色のインデックスを取得
                  const currentColorIndex = POUCH_COLORS.findIndex(color => color.id === targetPouch.color);
                  
                  let nextColorId: string;
                  
                  if (isShiftPressed) {
                    // Shift+クリック: 逆ローテーション
                    if (currentColorIndex === 0) {
                      nextColorId = POUCH_COLORS[POUCH_COLORS.length - 1].id;
                    } else {
                      const prevColorIndex = (currentColorIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
                      nextColorId = POUCH_COLORS[prevColorIndex].id;
                    }
                  } else {
                    // 通常クリック: 通常のローテーション
                    const nextColorIndex = (currentColorIndex + 1) % POUCH_COLORS.length;
                    nextColorId = currentColorIndex === -1 
                      ? POUCH_COLORS[0].id 
                      : POUCH_COLORS[nextColorIndex].id;
                  }
                  
                  // パウチの色を更新
                  const updatedPouches = activeSheet.pouches.map(pouch => {
                    if (pouch.id === pouchId) {
                      return {
                        ...pouch,
                        color: nextColorId
                      };
                    }
                    return pouch;
                  });
                  
                  // handleUpdateActiveSheetを呼び出して保存
                  await handleUpdateActiveSheet(updatedPouches, null, false, true, undefined, HistoryActionType.CHANGE_POUCH_COLOR);
                }
              }}
              onChangeGroupColor={async (groupId, isShiftPressed = false, isCtrlOption = false, skipMute = false) => {
                if (activeSheet) {
                  let updatedGroups: Group[];
                  if (isCtrlOption && !skipMute) {
                    const targetGroup = activeSheet.groups.find(g => g.id === groupId);
                    if (targetGroup) {
                      if (targetGroup.color === 'mute') {
                        updatedGroups = activeSheet.groups.map(g =>
                          g.id === groupId ? { ...g, color: g.originalColor || 'yellow' } : g
                        );
                      } else {
                        updatedGroups = activeSheet.groups.map(g =>
                          g.id === groupId ? { ...g, color: 'mute', originalColor: g.color } : g
                        );
                      }
                    } else {
                      return;
                    }
                  } else if (isShiftPressed) {
                    updatedGroups = changeGroupColor(groupId, activeSheet.groups, true, skipMute);
                  } else {
                    updatedGroups = changeGroupColor(groupId, activeSheet.groups, false, skipMute);
                  }
                  await handleUpdateActiveSheet(null, updatedGroups, false, true, undefined, HistoryActionType.CHANGE_GROUP_COLOR);
                }
              }}
              isTouchDevice={state.isTouchDevice}
              isInitialLoading={isInitialLoading}
              isSheetSwitching={isSheetSwitchingRef.current}
            />
          )}
        </div>
      </div>

      {/* カスタム確認ダイアログ（PWA対応） */}
      {showImportConfirm && (
        <div className="custom-prompt-overlay">
          <div className="custom-prompt-dialog">
            <div className="custom-prompt-header">
              Import Confirmation
            </div>
            <div className="custom-prompt-body">
              <p className="custom-prompt-message">
                Import POUCHES Sheet: <strong>{confirmSheetTitle}</strong>?
              </p>
            </div>
            <div className="custom-prompt-footer">
              <button 
                className="custom-prompt-button cancel"
                onClick={handleConfirmCancel}
                onTouchEnd={handleConfirmCancel}
              >
                Cancel
              </button>
              <button 
                className="custom-prompt-button ok"
                onClick={handleConfirmOK}
                onTouchEnd={handleConfirmOK}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* カスタムエクスポート確認ダイアログ（PWA対応） */}
      {showExportConfirm && (
        <div className="custom-prompt-overlay">
          <div className="custom-prompt-dialog">
            <div className="custom-prompt-header">
              Export POUCHES Sheet
            </div>
            <div className="custom-prompt-body">
              <p className="custom-prompt-message">
                How would you like to export <strong>{exportSheetTitle}</strong>?
              </p>
            </div>
            <div className="custom-prompt-footer export-buttons">
              <button 
                className="custom-prompt-button private"
                onClick={() => handleExportAction('private')}
                onTouchEnd={() => handleExportAction('private')}
              >
                Private Share
              </button>
              <button 
                className="custom-prompt-button public"
                onClick={() => handleExportAction('public')}
                onTouchEnd={() => handleExportAction('public')}
              >
                Public Share
              </button>
              <button 
                className="custom-prompt-button download"
                onClick={() => handleExportAction('download')}
                onTouchEnd={() => handleExportAction('download')}
              >
                Download
              </button>
              <button 
                className="custom-prompt-button cancel"
                onClick={handleExportCancel}
                onTouchEnd={handleExportCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* カスタムインポートプロンプト（PWA対応） */}
      {showImportPrompt && (
        <div className="custom-prompt-overlay">
          <div className="custom-prompt-dialog">
            <div className="custom-prompt-header">
              Import POUCHES Sheet
            </div>
            <div className="custom-prompt-body">
              <p className="custom-prompt-message">
                Enter import code or full URL, or leave empty to select .pouches file:
              </p>
              <input
                type="text"
                className="custom-prompt-input"
                value={importInputValue}
                onChange={(e) => setImportInputValue(e.target.value)}
                placeholder="e.g., 1756489555964_sah6mrm5bfe"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleImportPromptOK();
                  } else if (e.key === 'Escape') {
                    handleImportPromptCancel();
                  }
                }}
                onTouchEnd={(e) => e.stopPropagation()}
              />
            </div>
            <div className="custom-prompt-footer">
              <button 
                className="custom-prompt-button cancel"
                onClick={handleImportPromptCancel}
                onTouchEnd={handleImportPromptCancel}
              >
                Cancel
              </button>
              <button 
                className="custom-prompt-button ok"
                onClick={handleImportPromptOK}
                onTouchEnd={handleImportPromptOK}
              >
                {importInputValue.trim() === '' ? 'Select File...' : 'Import from URL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <Tooltip id="tt" delayShow={0} delayHide={80} offset={8} className="app-tooltip" noArrow={true} />
    </>
    </GuideContext.Provider>);
}

export default App; 